import { useState, lazy, useEffect, useRef, Suspense } from "react"
import moment from "moment"
import ReactPixel from "react-facebook-pixel"

// import Geocode from "react-geocode"
import socketIOClient from "socket.io-client"
import { loadMercadoPago } from "@mercadopago/sdk-js"
import { Button, Label } from "reactstrap"
import { ErrorBoundary } from "react-error-boundary"
import toast, { Toaster } from "react-hot-toast"
import Skeleton from "react-loading-skeleton"

import "moment/locale/pt-br"

//Contexts
import TabContext from "./contexts/TabContext"
import MenuContext from "./contexts/MenuContext"
import UserContext from "./contexts/UserContext"
import OrderContext from "./contexts/OrderContext"
import SocketContext from "./contexts/SocketContext"
import LoadingContext from "./contexts/LoadingContext"
import CompanyContext from "./contexts/CompanyContext"
import SettingsContext from "./contexts/SettingsContext"
import MercadoPagoContext from "./contexts/MercadoPagoContext"

//Controllers
import UserController from "./controllers/UserController"
import OrderController from "./controllers/OrderController"
import PopupController from "./controllers/PopupController"
import ComboController from "./controllers/ComboController"
import ProductController from "./controllers/ProductController"
import CompanyController from "./controllers/CompanyController"
import BenefitController from "./controllers/BenefitController"
import SettingController from "./controllers/SettingController"
import SupportController from "./controllers/SupportController"
import FidelityController from "./controllers/FidelityController"
import CustomerController from "./controllers/CustomerController"
import BeverageController from "./controllers/BeverageController"
import ScheduleController from "./controllers/ScheduleController"
import CredentialController from "./controllers/CredentialController"
import HighlightsController from "./controllers/HighlightsController"
import DeliveryFeeController from "./controllers/DeliveryFeeController"
import DeliveryAreasController from "./controllers/DeliveryAreasController"
import MercadoPagoController from "./controllers/MercadoPagoController"
import PaymentsController from "./controllers/PaymentsController"

// Helpers
import { getDiscountOrder, getParamsToCreateOrder, getTotalOrder } from "./helpers/OrderTools"
import { getBeverageName, getProductName } from "./helpers/BuilderTools"
import { userIsConnected, getLocalUser, getFidelityProgress } from "./helpers/UserTools"
import GeneralTools, {
  formatBoolean,
  onMessageDialog,
  formatFloat,
  getFormatDate,
  getUrlAWSManagerFile,
  escapeSpecialChart,
} from "./helpers/GeneralTools"
import { checkBetweenIntervalPeriod } from "./helpers/DateTools"
import {
  getCity,
  getLabelAddress,
  getNeighbourhood,
  getState,
  getStates,
  getStreetName,
  getDistanceCoordinates,
  getAddressSuggestionsByName,
  getAddressCoordinates,
} from "./helpers/AddressessTools"
import {
  encrypt,
  getLocalItem,
  getSessionItem,
  removeLocalItem,
  removeSessionItem,
  setLocalItem,
  setSessionItem,
  getCookie,
  setCookie,
  removeCookie,
} from "./helpers/StorageTools"

// Modal
import ModalClientAddressNotFound from "./views/modals/ModalClientAddressNotFound/ModalClientAddressNotFound"
import ModalMinimapAddress from "./views/modals/ModalMinimapAddress/ModalMinimapAddress"

// Components
import FacebookPixelManager from "./components/FacebookPixelManager"
import FullContentLoading from "./components/FullContentLoading"

// Sytles
import logoImage from "./assets/img/brand/logo.png"

import "./App.scss"

const Header = lazy(() => import("./components/Header"))
const Footer = lazy(() => import("./components/Footer"))

// Componentes da UI (alguns podem ser sempre visíveis, outros podem ser lazy-carregados)
const OrderResumeBar = lazy(() => import("./components/OrderResumeBar"))
const Message = lazy(() => import("./components/Message")) // Se usado globalmente, talvez não seja bom lazy load

// Componentes de Páginas/Seções - ALTAMENTE RECOMENDADO O LAZY LOAD
const Main = lazy(() => import("./views/sections/Main"))
const Highlights = lazy(() => import("./views/sections/Highlights"))
const Combos = lazy(() => import("./views/sections/Combos"))
const Products = lazy(() => import("./views/sections/Products"))
const Beverages = lazy(() => import("./views/sections/Beverages"))
const Profile = lazy(() => import("./components/Profile/Profile"))

// Modais (já transformados no exemplo anterior)
const ModalBuilder = lazy(() => import("./views/modals/ModalBuilder"))
const OrderPreview = lazy(() => import("./components/OrderPreview/OrderPreview")) // É um modal, faz sentido ser lazy

// Modals
const ModalPopup = lazy(() => import("./views/modals/ModalPopup"))
const ModalSignIn = lazy(() => import("./views/modals/ModalSignIn"))
const ModalBlocked = lazy(() => import("./views/modals/ModalBlocked"))
const ModalCompanies = lazy(() => import("./views/modals/ModalCompanies"))
const ModalBeverages = lazy(() => import("./views/modals/ModalBeverages"))
const ModalSuccessOrder = lazy(() => import("./views/modals/ModalSuccessOrder"))
const ModalOrderFinisher = lazy(() => import("./views/modals/ModalOrderFinisher/ModalOrderFinisher"))
const ModalOrderProcess = lazy(() => import("./views/modals/ModalOrderProcess/ModalOrderProcess"))
const ModalOrderSchedule = lazy(() => import("./views/modals/ModalOrderSchedule/ModalOrderSchedule"))
const ModalInitialAddress = lazy(() => import("./views/modals/ModalInitialAddress/ModalInitialAddress"))
const ModalMapAddress = lazy(() => import("./views/modals/ModalMapAddress/ModalMapAddress"))
const ModalNumberAddress = lazy(() => import("./views/modals/ModalNumberAddress/ModalNumberAddress"))
const ModalFinalAddress = lazy(() => import("./views/modals/ModalFinalAddress/ModalFinalAddress"))
const ModalCompanyContacts = lazy(() => import("./views/modals/ModalCompanyContacts/ModalCompanyContacts"))
const ModalAddressNotFound = lazy(() => import("./views/modals/ModalAddressNotFound/ModalAddressNotFound"))
const ModalPixPayment = lazy(() => import("./views/modals/ModalPixPayment/ModalPixPayment"))

moment.locale("pt-br")
const App = () => {
  const version = process.env.REACT_APP_VERSION

  const [errorMessage, setErrorMessage] = useState([])

  const [loading, setLoading] = useState(true)
  const [forceLoading, setForceLoading] = useState(false)
  const [addressesLoading, setAddressesLoading] = useState(false)

  const [order, setOrder] = useState({
    customer: null,
    combos: [],
    products: [],
    beverages: [],
    payments: [],
    benefits: [],
    total: 0.0,
    subTotal: 0.0,
    discount: 0,
    discountType: "R$",
    deliveryFee: 0,
    deliveryComments: "",
  })
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState({
    id_payment_method: -1,
  })

  const [currentOrderCombo, setCurrentOrderCombo] = useState(0)

  const [user, setUser] = useState(null)
  const [socket, setSocket] = useState(null)
  const [company, setCompany] = useState({})

  const [tabSelected, setTabSelected] = useState("home")
  const [hideTabs, setHideTabs] = useState(false)

  const [menuFixed, setMenuFixed] = useState(false)
  const [menuOptions, setMenuOptions] = useState([])

  const [combos, setCombos] = useState([])
  const [settings, setSettings] = useState([])
  const [products, setProducts] = useState([])
  const [popup, setPopup] = useState(null)
  const [highlights, setHighlights] = useState([])
  const [beverages, setBeverages] = useState([])
  const [schedules, setSchedules] = useState([])
  const [companies, setCompanies] = useState([])
  const [fidelities, setFidelities] = useState([])
  const [modality, setModality] = useState(1) // 1 - entreta / 2 - retirada
  const [currentStep, setCurrentStep] = useState({ step: 0, date: "00:00" })

  const [deliveryType, setDeliveryType] = useState("direct")

  const [isOutsideCoverageArea, setIsOutsideCoverageArea] = useState(false)
  const [finalizingOrder, setFinalizingOrder] = useState(false)

  const [states, setStates] = useState([])
  const [neighbourhoods, setNeighbourhoods] = useState([])

  const [userSelectedAddress, setUserSelectedAddress] = useState(null)
  const [modalContentOrder, setModalContentOrder] = useState(false)
  const [modalSignIn, setModalSignIn] = useState({ status: false })
  const [modalOrderFinisher, setModalOrderFinisher] = useState(false)
  const [modalOrderProcess, setModalOrderProcess] = useState({
    status: false,
    content: null,
    resend: false,
    newRequest: false,
    scheduleInfo: null,
  })
  const [modalOrderPreview, setModalOrderPreview] = useState(false)
  const [modalOrderSchedule, setModalOrderSchedule] = useState(false)
  const [modalCompanyContacts, setModalCompanyContacts] = useState(false)
  const [modalCompanies, setModalCompanies] = useState({ status: false, content: null, earlyAddress: null })
  const [modalPaymentMethods, setModalPaymentMethods] = useState({ status: false, content: null })
  const [modalBuilder, setModalBuilder] = useState({ status: false, type: "product", content: null })
  const [modalBeverages, setModalBeverages] = useState({ status: false, content: null })
  const [modalSuccessOrder, setModalSuccessOrder] = useState({ status: false, content: null })
  const [modalPopup, setModalPopup] = useState({ status: true })
  const [modalBlockByOutdatedVersion, setModalBlockByOutdatedVersion] = useState({ status: false, content: null })
  const [modalClientAddressNotFound, setModalClientAddressNotFound] = useState({ status: false, address: null })
  const [modalAddressNotFound, setModalAddressNotFound] = useState({ status: false, title: "", companies: [] })
  const [modalPixPayment, setModalPixPayment] = useState({ status: false, content: null })
  const [modalMinimapAddress, setModalMinimapAddress] = useState({ status: false, content: null })

  const [modalInitialAddress, setModalInitialAddress] = useState({ status: false, content: null })
  const [modalMapAddress, setModalMapAddress] = useState({ status: false, location: {} })
  const [modalNumberAddress, setModalNumberAddress] = useState({ status: false, address: {} })
  const [modalFinalAddress, setModalFinalAddress] = useState({ status: false, address: {}, editNeighbourhood: false })
  const [showPaymentTypeSelector, setShowPaymentTypeSelector] = useState(false)
  const [recoverySettings, setRecoverySettings] = useState({})

  const [colors, setColors] = useState([
    { id: 1, used: false, color: "#F2D7D5" },
    { id: 2, used: false, color: "#D4E6F1" },
    { id: 3, used: false, color: "#D4EFDF" },
    { id: 4, used: false, color: "#FDEBD0" },
    { id: 5, used: false, color: "#FADBD8" },
    { id: 6, used: false, color: "#EBDEF0" },
    { id: 7, used: false, color: "#D6EAF8" },
    { id: 8, used: false, color: "#D1F2EB" },
    { id: 9, used: false, color: "#D6DBDF" },
    { id: 10, used: false, color: "#F4ECF7" },
    { id: 11, used: false, color: "#FAE5D3" },
  ])
  const [updateOrders, setUpdateOrders] = useState(false)
  const [activePixelId, setActivePixelId] = useState({ companies: [], pixelId: null })

  const companyRef = useRef({ uuid_company: null })
  const menuOptionsRef = useRef(menuOptions)
  const deliveryFeesRef = useRef(null)
  const deliveryAreasRef = useRef(null)
  const orderSuccessId = useRef(null)
  const popupAlreadyOpen = useRef(false)
  const goToOrderProgressRef = useRef({ status: false, orderUid: null, scheduleOrderUid: null })
  const modalPixPaymentContent = useRef(null)
  const goToFidelityRef = useRef(false)
  const goToOrdersRef = useRef(false)
  const editAddressRef = useRef(null)
  const menuFixedRef = useRef(menuFixed)
  const finalizingOrderRef = useRef(false)
  const userRef = useRef(null)
  const scheduleInfoRef = useRef(null)
  const userSelectedAddressRef = useRef(null)
  const groupFidelitiesRef = useRef({})
  const showOrderProgressContextRef = useRef({ status: false, orderUid: null, scheduleOrderUid: null })

  // Objeto para armazenar a instância do mercado pago
  const [MercadoPagoInstance, setMercadoPagoInstance] = useState({})

  /* eslint-disable */
  useEffect(() => {
    setLoading(true)

    const searchParams = new URLSearchParams(document.location.search)

    const customerUid = searchParams.get("c")
    const expiresIn = searchParams.get("e")

    if (customerUid !== null || expiresIn !== null) {
      setRecoverySettings({ customerUid: customerUid, expiresIn: expiresIn })
      setModalSignIn({ status: true })
    }

    //Verificando se o cliente está na versão atual correta
    //Caso não esteja, zerar o localstorage e cookies para evitar erros
    verifyWebsiteVersion().then()

    getCookie("publicToken")
      .then(async (publicToken) => {
        let storageCompany = getLocalItem("company", true)
        let storageCompanyIds = getLocalItem("companyIds", true)

        let hostStorage = getLocalItem("siteHost")

        if (!publicToken || hostStorage !== window.location.host) {
          // Obtendo as credenciais do assinante
          CredentialController.fetchOne().then((responseCredentials) => {
            if (responseCredentials.status === 200) {
              const [subscriberUid, accessToken, database, companyIds = [], group = null] = responseCredentials.data
              if (hostStorage !== window.location.host) {
                storageCompany = null
              }

              publicToken = accessToken
              setLocalItem("database", database)
              if (group) setLocalItem("group", group)
              setLocalItem("companyIds", companyIds, true)
              setLocalItem("siteHost", window.location.host)
              setCookie("subscriberUid", subscriberUid).then()
              setCookie("publicToken", publicToken).then(() => {
                setOnStartInfo(publicToken, storageCompany, companyIds)
              })
            } else {
              setLoading(false)
              setErrorMessage([
                "Não foi possível carregar o conteúdo do site.",
                "Favor atualizar a página! ",
                "Caso o erro persista, favor reportar imediatamente ao estabelecimento! ",
              ])
            }
          })
        } else {
          setOnStartInfo(publicToken, storageCompany, storageCompanyIds)
        }
      })
      .catch((error) => {
        console.error("ERROR", error)
        setLoading(false)
        setErrorMessage(["Não foi possível carregar o conteúdo do site.", "Favor reportar imediatamente ao estabelecimento! "])
      })

    // Eventos de rolagem da página
    document.getElementById("section-home") &&
      document.getElementById("section-home").addEventListener("scroll", () => {
        document.getElementById("section-home").scrollHeight

        // Verificando se o rolar da página já chegou na posição do tamanho do cabeçalho
        // as vezes o getGlement não renderiza e por isso tem que validar antes.

        if (
          document.getElementById("header") &&
          document.getElementById("section-home").scrollTop >= document.getElementById("header").offsetHeight
        ) {
          if (menuFixedRef.current === false) {
            menuFixedRef.current = true
            setMenuFixed(true)
          }
          // setSidebarMenu(false)
        } else {
          if (menuFixedRef.current === true) {
            menuFixedRef.current = false
            setMenuFixed(false)
          }
          // setSidebarMenu(true)
        }

        for (const { id } of menuOptionsRef.current) {
          if (!!document.getElementById(id)) {
            // Definindo o valor da seção de acordo com o tamanho e posição na página
            const offset = document.getElementById(id).offsetTop + document.getElementById(id).offsetHeight - 200

            // Se for maior significa que está sendo mostrado
            if (offset > document.getElementById("section-home").scrollTop) {
              const newMenuOptions = menuOptionsRef.current.map((item) => {
                return { ...item, focus: item.id === id }
              })

              // Mudando o item do menu ativo
              menuOptionsRef.current = [...newMenuOptions]

              // setMenuOptions([...newMenuOptions])

              // const menuFixed = document.getElementsByClassName('menu-options-fixed')[0]
              //
              // if (!!menuFixed) {
              //     const menuChildren = Array.from(menuFixed.children)
              //     const optionIndex = menuChildren.findIndex(item => item.className === 'active')
              //
              //     if (optionIndex !== -1) {
              //
              //         if (optionIndex === 0) {
              //             menuFixed.scrollTo(0, 0)
              //         } else if (optionIndex === menuChildren.length - 1) {
              //             menuFixed.scrollTo(menuFixed.offsetWidth, 0)
              //         } else {
              //             menuFixed.scrollTo(menuChildren[optionIndex].offsetWidth, 0)
              //         }
              //     }
              // }

              break
            }
          }
        }
      })
  }, [])

  useEffect(() => {
    showRecentPendingPayment()
  }, [userRef.current])

  useEffect(() => {
    const popupStorage = getLocalItem("popups", true)
    if (!!popupStorage) {
      const companyPopup = popupStorage?.data?.rows?.find(
        ({ fk_id_company, fk_id_subscriber }) =>
          company.id_company === fk_id_company && company?.subscriber?.id_subscriber === fk_id_subscriber && !modalCompanies.status
      )
      if (!!companyPopup && !popupAlreadyOpen.current) {
        popupAlreadyOpen.current = true
        showPopupMessage(companyPopup, companyPopup)
      }
    }
  }, [company])

  useEffect(() => {
    getDiscountType()
  }, [modality])

  const verifyWebsiteVersion = async () => {
    const buildVersion = process.env.REACT_APP_VERSION
    const currentWebsiteVersion = await SupportController.getWebsiteVersion()
    if (currentWebsiteVersion.status === 200) {
      const currentWebsiteVersionList = (currentWebsiteVersion?.data?.nro_version || "0.0.0").split(".").map(Number)
      const buildVersionList = buildVersion.split(".").map(Number)

      // if (currentWebsiteVersion.data.nro_version !== buildVersion) {
      if (
        currentWebsiteVersionList[0] !== buildVersionList[0] ||
        currentWebsiteVersionList[1] !== buildVersionList[1] ||
        buildVersionList[2] < currentWebsiteVersionList[2]
      ) {
        setModalBlockByOutdatedVersion({
          status: true,
          content: {
            buildVersion,
            currentVersion: currentWebsiteVersion.data.nro_version,
          },
        })
      }
    } else {
      setModalBlockByOutdatedVersion({
        status: true,
        content: {
          buildVersion,
          currentVersion: null,
          getWebsiteVersionFailed: true,
        },
      })
    }
  }

  const showRecentPendingPayment = async () => {
    if (!!userRef.current) {
      const pendingPaymentsRequest = await PaymentsController.getRecentPendingPayments(userRef.current.uuid_customer)

      if (pendingPaymentsRequest.status === 200 && !modalPixPayment.status) {
        const pendingPayment = pendingPaymentsRequest.data

        const paymentInfoRequest = await PaymentsController.getPaymentInfo(pendingPayment.payment_id)

        if (paymentInfoRequest.status === 200) {
          setModalPixPayment({ status: true, content: paymentInfoRequest.data })
        }
      }
    }
  }

  // Buscar configurações de desconto por modalidade
  const getDiscountType = (modalityOrder, changeOrder) => {
    let newOrder = { ...order }
    let discountType = "R$"
    let discount = "0,00"

    let newModality = !!modalityOrder ? modalityOrder : modality
    // if (!!modality) {
    switch (newModality) {
      case 1:
        if (!!settings.delivery_discount_type) {
          discountType = settings.delivery_discount_type
          newOrder = { ...newOrder, discountType }
        }

        if (!!settings.delivery_discount_value) {
          const [total, subTotal] = getTotalOrder(newOrder, userSelectedAddress?.deliveryFee)
          const discount = parseFloat(settings.delivery_discount_value)
          newOrder = { ...newOrder, discount, ds_discount: discount > 0 ? "Desconto de modalidade" : "" }
        }
        if (changeOrder) {
          setOrder({ ...newOrder })
        }
        return { discountType, discount }
      case 2:
        if (!!settings.pickup_discount_type) {
          discountType = settings.pickup_discount_type
          newOrder = { ...newOrder, discountType }
        }

        if (!!settings.pickup_discount_value) {
          const discount = parseFloat(settings.pickup_discount_value)
          newOrder = { ...newOrder, discount, ds_discount: discount > 0 ? "Desconto de modalidade" : "" }
        }
        if (changeOrder) {
          setOrder({ ...newOrder })
        }
        return { discountType, discount }
      case 3:
        if (!!settings.indoor_discount_type) {
          discountType = settings.indoor_discount_type

          newOrder = { ...newOrder, discountType }
        }

        if (!!settings.indoor_discount_type) {
          const discount = parseFloat(settings.indoor_discount_value)
          newOrder = { ...newOrder, discount, ds_discount: discount > 0 ? "Desconto de modalidade" : "" }
        }
        if (changeOrder) {
          setOrder({ ...newOrder })
        }
        return { discountType, discount }
      default:
        newOrder = { ...newOrder, discountType, discount }
        if (setOrder) {
          setOrder({ ...newOrder })
        }
        return { discountType, discount }
    }
    // }
  }

  // Busca de pedidos por agendamentos
  const getScheduleOrders = async () => {
    const dateNow = new Date()
    const dateStartEnd = new Date()
    dateStartEnd.setDate(dateStartEnd.getDate() + 7)

    const responseScheduleOrders = await ScheduleController.fetchAllScheduleOrders({
      startDate: moment(dateNow).format("YYYY-MM-DD"),
      endDate: moment(dateStartEnd).format("YYYY-MM-DD"),
    })

    if (responseScheduleOrders.status === 200) {
      return responseScheduleOrders.data.rows || []
    }

    onMessageDialog("danger")
      .fire({
        title: "Erro ao buscar dados dos agendamentos do estabelecimento",
        icon: "error",
        showConfirmButton: true,
        confirmButtonText: "Aceitar",
        showCancelButton: true,
        cancelButtonText: "Recusar",
        reverseButtons: true,
      })
      .then()

    return []
  }

  // Busca dos dias dos agendamentos e seus horários ( Storage / Banco )
  const getSchedules = async (new_st_open = null, withOrders = false, config_orders) => {
    const weekdayDefault = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"]

    const now_day = new Date().getDay() // Dia atual ( Hoje )

    let schedules = []
    let ordersSchedules = []

    const schedulesSession = await getLocalItem("schedules", true)

    if (!!schedulesSession) {
      const scheduleList = schedulesSession.map(({ uuid_schedule, day_of_week, st_open, work_times, neighbourhoods }) => {
        return {
          uuid_schedule,
          day_of_week,
          st_open:
            new_st_open !== null
              ? day_of_week === now_day
                ? new_st_open
                : st_open
              : day_of_week === now_day
              ? config_orders
                ? company?.st_website
                : st_open
              : st_open,
          work_times,
          neighbourhoods,
          name: weekdayDefault[day_of_week],
        }
      })
      ordersSchedules = await getScheduleOrders()

      schedules = scheduleList
    } else {
      await ScheduleController.fetchAll()
        .then(async (responseSchedule) => {
          if (responseSchedule.status === 200) {
            const scheduleList = responseSchedule.data.map(({ uuid_schedule, day_of_week, st_open, work_times, neighbourhoods }) => {
              return {
                uuid_schedule,
                day_of_week,
                st_open:
                  new_st_open !== null
                    ? day_of_week === now_day
                      ? new_st_open
                      : st_open
                    : day_of_week === now_day
                    ? config_orders
                      ? company?.st_website
                      : st_open
                    : st_open,
                work_times,
                neighbourhoods,
                name: weekdayDefault[day_of_week],
              }
            })

            schedules = scheduleList

            if (withOrders) {
              ordersSchedules = await getScheduleOrders()
            }
          } else {
            onMessageDialog("danger")
              .fire({
                title: "Erro ao buscar dados de agendamento",
                icon: "error",
                showConfirmButton: true,
                confirmButtonText: "Aceitar",
                showCancelButton: true,
                cancelButtonText: "Recusar",
                reverseButtons: true,
              })
              .then()
          }
        })
        .catch(() => {
          onMessageDialog("danger")
            .fire({
              title: "Erro ao buscar dados de agendamento",
              icon: "error",
              showConfirmButton: true,
              confirmButtonText: "Aceitar",
              showCancelButton: true,
              cancelButtonText: "Recusar",
              reverseButtons: true,
            })
            .then()
        })
    }

    return { schedules, ordersSchedules }
  }

  // Dados do socket
  const setOnSocket = (publicToken, database, group) => {
    const socket = socketIOClient(process.env.REACT_APP_CF_SOCKET_HOST, {
      transports: ["websocket"],
      query: { token: publicToken, origin: "site-subscriber", database, group },
    })

    socket.on("store_openned", async () => {
      console.info("RECEBENDO DADOS DO SOCKET [Home] [store_openned]")
      onChangeStatusCompany(true)

      // Abrindo a loja no dia, alteramos os status do dia para fazer agendamentos
      const { schedules } = await getSchedules(true)

      if (!!schedules) {
        setLocalItem("schedules", schedules, true)
      }
    })

    socket.on("store_closed", async () => {
      console.info("RECEBENDO DADOS DO SOCKET [Home] [store_closed]")
      onChangeStatusCompany(false)

      // Alterando status do dia atual para false ( caso a loja feche no meio da operação )
      const { schedules } = await getSchedules(formatBoolean(settings?.allow_order_schedule_with_closed_company) ? true : false)

      if (!!schedules) {
        setLocalItem("schedules", schedules, true)
      }
    })

    socket.on("online_payment_approved", async (socketInfo) => {
      console.info("RECEBENDO DADOS DO SOCKET [Home] [online_payment_approved]")
      if (companyRef.current?.id_company === socketInfo.fk_id_company && socketInfo.uuid_customer === userRef.current?.uuid_customer) {
        goToOrderProggressAfterOnlinePayment(socketInfo.order.uuid_order)
      }
    })

    //Estabelecimento atualizou o tempo de retirada
    socket.on("pickup_time_updated", (socketinfo) => {
      if (company.id_company === socketinfo.companyId || companyRef.current.id_company === socketinfo.companyId) {
        console.info("RECEBENDO DADOS DO SOCKET [Home] [pickup_time_updated]")
        setSettings({ ...settings, pickup_time: socketinfo.pickup_time })
      }
    })

    setSocket(socket)
  }

  const goToOrderProggressAfterOnlinePayment = async (uuidOrder) => {
    clearOrder()
    toast.success("Pedido confirmado com sucesso!")
    setModalPixPayment({ status: false, content: null })

    const actualOrder = await OrderController.fetchOne(uuidOrder)

    setTabSelected("profile")

    showOrderProgressContextRef.current = { status: true, orderUid: uuidOrder, content: actualOrder.data }
    setUpdateOrders(!updateOrders)
  }

  //Zerar os dados do storage e cookies
  // const clearStorageData = async () => {
  //   await removeCookie("publicToken")
  //   await removeCookie("subscriberUid")
  //   removeLocalItem("menuOptions")
  //   removeLocalItem("settings")
  //   removeLocalItem("highlights")
  //   removeLocalItem("combos")
  //   removeLocalItem("products")
  //   removeLocalItem("productSizes")
  //   removeLocalItem("productFlavours")
  //   removeLocalItem("beverages")
  //   removeLocalItem("schedules")
  //   removeLocalItem("deliveryFee")
  //   removeLocalItem("deliveryTime")
  //   removeLocalItem("userSelectedAddress")
  //   removeLocalItem("user")
  //   removeLocalItem("siteHost")
  //   removeLocalItem("orderItems")
  //   removeLocalItem("deliveryFees")
  //   removeLocalItem("states")
  //   removeLocalItem("streettypes")
  //   removeLocalItem("modality")
  //   removeLocalItem("location")
  //   removeLocalItem("popups")
  // }

  const setOnStartInfo = async (publicToken, storageCompany = null, companyIds = []) => {
    const database = getLocalItem("database")
    const group = getLocalItem("group")
    const menuOptionsStorage = getLocalItem("menuOptions", true)

    if (menuOptionsStorage) {
      setMenuOptions(menuOptionsStorage)
      menuOptionsRef.current = menuOptionsStorage
    }

    console.info("CONECTANDO NO SOCKET...")
    setOnSocket(publicToken, database, group)

    console.info("CONSULTANDO DADOS DE ESTABELECIMENTOS...")
    let newCompany = storageCompany

    const searchParams = new URLSearchParams(document.location.search)

    const whatsappNumber = searchParams.get("wn")
    const companyIdString = searchParams.get("cId")

    if (whatsappNumber !== null) {
      setForceLoading(true)

      if (!!newCompany) {
        const whatsappCompanyId = parseInt(companyIdString)

        if (newCompany.id_company !== whatsappCompanyId) {
          await setLocalCompany(storageCompany, companyIds, whatsappCompanyId)
        }
      }

      const parsedWhatsappNumber = atob(whatsappNumber)

      const localUser = getLocalUser()

      let makeLogin = false

      if (!!localUser) {
        sessionStorage.clear()
        removeLocalItem("user")
        setUserSelectedAddress(null)
        setUser(null)
        setOrder({ ...order, benefits: [] })

        makeLogin = true
      } else {
        makeLogin = true
      }

      if (makeLogin) {
        setTabSelected("home")

        onSignIn(parsedWhatsappNumber.replace("55", ""), null, true)
          .then(async () => {
            const trackingId = searchParams.get("r")

            if (trackingId !== null) {
              const orderUid = atob(trackingId)

              const actualOrder = await OrderController.fetchOne(orderUid)

              setTabSelected("profile")

              showOrderProgressContextRef.current = { status: true, orderUid: orderUid, content: actualOrder.data }
              setUpdateOrders(!updateOrders)
            }
          })
          .finally(() => {
            setForceLoading(false)

            window.history.pushState({}, document.title, "/" + "")
          })
      }
    }

    const parsedCompanyId = parseInt(companyIdString)
    await setLocalCompany(storageCompany, companyIds, !!parsedCompanyId && !isNaN(parsedCompanyId) ? parsedCompanyId : null)
  }

  const setEditAddressRef = (editAddress) => {
    editAddressRef.current = editAddress
  }

  const removeMenuOption = (option) => {
    let localMenuOptions = getLocalItem("menuOptions", true)
    if (!!localMenuOptions) {
      if (option !== "products") {
        const removeIndex = localMenuOptions.findIndex(({ id }) => id === option)
        localMenuOptions.splice(removeIndex, 1)
      } else {
        localMenuOptions = localMenuOptions.filter(({ id }) => ["highlights", "combos", "beverages"].includes(id))
      }

      setMenuOptions(localMenuOptions)
      menuOptionsRef.current = localMenuOptions
      setLocalItem("menuOptions", localMenuOptions, true)
    }
  }

  const addMenuOption = (content) => {
    let newMenuOptions
    // Consultando dados do armazenamento local
    const localMenuOptions = getLocalItem("menuOptions", true)

    if (!!localMenuOptions && localMenuOptions.length > 0) {
      const indexMenu = localMenuOptions.findIndex(({ id }) => id === content.id)

      if (indexMenu !== -1) {
        localMenuOptions.splice(indexMenu, 1)
      }

      newMenuOptions = [...localMenuOptions, content]
    } else {
      const indexMenu = menuOptions.findIndex(({ id }) => id === content.id)

      if (indexMenu !== -1) {
        menuOptions.splice(indexMenu, 1)
      }

      newMenuOptions = [...menuOptions, content]
    }
    // Ordenando por posições definidas
    newMenuOptions.sort((a, b) => a.position - b.position)

    setMenuOptions(newMenuOptions)
    menuOptionsRef.current = newMenuOptions
    setLocalItem("menuOptions", newMenuOptions, true)
  }

  const updateMenu = (event, id) => {
    function verifyAndMoveToTargetElement(id) {
      var element = document.getElementById(id)
      var headerOffset = 45
      var elementPosition = element.getBoundingClientRect().top

      var offsetPosition = elementPosition + document.getElementById("section-home").scrollTop - headerOffset

      document.getElementById("section-home").scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      })
    }

    const newMenuOptions = menuOptionsRef.current.map((menu) => {
      return { ...menu, focus: false }
    })

    const indexMenu = newMenuOptions.findIndex((menu) => menu.id === id)

    if (indexMenu !== -1) {
      newMenuOptions[indexMenu] = {
        ...newMenuOptions[indexMenu],
        focus: true,
      }
    }

    menuOptionsRef.current = newMenuOptions

    if (!!document.getElementById(id)) {
      verifyAndMoveToTargetElement(id)

      // document.getElementById(id).scrollIntoView({ top: 20, behavior: "smooth", block: "start", inline: "start" })
    } else {
      window.scrollTo(0, 0)
    }

    setMenuOptions(menuOptionsRef.current)
  }

  const onClearUserData = () => {
    setUser(null)
    userRef.current = null
    setUserSelectedAddress(null)
    setLocalItem("user", null, true)
    setLocalItem("userSelectedAddress", null, true)
    sessionStorage.clear()
    removeLocalItem("user")
    setOrder({
      customer: null,
      combos: [],
      products: [],
      beverages: [],
      payments: [],
      benefits: [],
      total: 0.0,
      subTotal: 0.0,
      comments: "",
      discount: "0,00",
      discountType: "R$",
      deliveryFee: 0,
      deliveryComments: "",
    })
    removeSessionItem("order")
  }

  //Buscar dados do sistema
  const getContent = async (company, startLoad = false, refreshOrder = false, companies = []) => {
    let localUser = getLocalUser()

    const group = getLocalItem("group")
    const database = getLocalItem("database")
    const previousDatabase = database
    if (!!group && group !== "null" && !!company && !!database && !!company.database && database !== company?.database && !!company.subscriber) {
      console.info("Trocou database")
      setLocalItem("database", company.database)
      await setCookie("publicToken", company.subscriber.ds_access_token)
    }

    let focusMenuOption = true
    setCompany(company)
    companyRef.current = company
    setLocalItem("company", company, true)

    const storageCompanies = getLocalItem("companies", true)
    setCompanies(storageCompanies)
    let orderItems = getLocalItem("orderItems", true) || []
    if (refreshOrder) {
      setOrder({
        customer: null,
        combos: [],
        products: [],
        beverages: [],
        payments: [],
        benefits: [],
        total: 0.0,
        subTotal: 0.0,
        comments: "",
        discount: "0,00",
        discountType: "R$",
        deliveryFee: 0,
        deliveryComments: "",
      })
      removeSessionItem("order")
    } else {
      // Verificando se existe pedido na sessão --------------------------------------------------------------
      const sessionOrder = getSessionItem("order", true)
      if (!!sessionOrder) {
        if (!!sessionOrder.combos && sessionOrder.combos.length > 0) {
          const comboNroList = sessionOrder.combos.map(({ nro_combo }) => nro_combo)
          const maxComboNro = Math.max(...comboNroList)
          setCurrentOrderCombo(maxComboNro)
        }
        setOrder({ ...sessionOrder })
      }
    }

    console.info("CONSULTANDO DADOS DE CONFIGURAÇÕES DO ESTABELECIMENTO...")
    // Obtendo dados das configurações armazenados no storage ------------------------------------------------------
    const settingsStorage = getLocalItem("settings", true)

    if (!!settingsStorage) {
      setLoading(false)
      setSettings(settingsStorage)

      // Verificando se é preciso exibir as modais de configuração inicial
      if (startLoad) {
        const userAddress = getLocalItem("userSelectedAddress", true)
        if (formatBoolean(settingsStorage.site_get_address_on_early_access)) {
          //Verifica se já existe um endereço no storage
          if (!userAddress) {
            setModalInitialAddress({ status: true, content: { source: "mainOptions" } })
          } else {
            //Verifica se está configurado para mostrar a tela de escolha das lojas
            if (formatBoolean(settingsStorage.site_display_company_choose) && companies.length > 1) {
              // if (ModalEarlyAddresses.status === false) {
              setModalCompanies({ status: true, content: storageCompanies })
              // }
              // setModalCompanies({ status: true, content: companies })
            }
          }
        } else if (formatBoolean(settingsStorage.site_display_company_choose) && storageCompanies.length > 1) {
          // if (ModalEarlyAddresses.status === false) {
          setModalCompanies({ status: true, content: storageCompanies })
          // }
          // setModalCompanies({ status: true, content: companies })
        }
      }

      if (!!settingsStorage.site_show_order) {
        orderItems = JSON.parse(settingsStorage.site_show_order)
        // setOrderMenuItems(orderItems)
        setLocalItem("orderItems", orderItems, true)
      }

      onChangeFavicon(getUrlAWSManagerFile(settingsStorage.site_favicon))
      onChangeTitlePage(settingsStorage.site_title || company.nm_company || "Estabelecimento")
    } else {
      setLoading(true)
    }

    await Promise.all([
      CompanyController.fetchOneSimple(),
      CompanyController.fetchPayments(),
      CompanyController.fetchWorkingPeriods(),
      CompanyController.fetchWorkingHours(),
      SettingController.fetchAllCategoryPaymentsMethods({ st_website: true }),
    ])
      .then(([responseCompany, responsePayments, responseWorkingPeriods, responseWorkingHours, responseCategoryPayments]) => {
        let enabledPayments = []
        let categoriesList = []

        if (responseCompany.status === 200) {
          const companyLocalData = storageCompanies.find(({ database = null, id_company }) => {
            const hasGroupData = group && company.database && database
            return id_company === responseCompany.data.id_company && (hasGroupData ? company.database === database : true)
          })
          company = { ...company, ...responseCompany.data, company_address: companyLocalData.company_address }

          if (!companyRef.current?.company_modality?.find(({ fk_id_modality }) => fk_id_modality === 1)) {
            setUserSelectedAddress(null)
            setLocalItem("userSelectedAddress", null, true)
            userSelectedAddressRef.current = null
            setModality(2)
          }
        }
        if (responsePayments.status === 200) {
          enabledPayments = responsePayments.data
            .filter((method) => method.st_website)
            .map((method) => {
              return {
                ds_key_payment_method: method.ds_key_payment_method,
                ds_key_type_payment_method: method.ds_key_type_payment_method,
                ds_obs_payment_method: method.ds_obs_payment_method,
                ...method.payment_method,
              }
            })

          company = { ...company, company_payment_methods: enabledPayments }
        }
        if (responseWorkingPeriods.status === 200) {
          company = { ...company, company_working_periods: responseWorkingPeriods.data }
        }
        if (responseWorkingHours.status === 200) {
          company = { ...company, company_working_hours: responseWorkingHours.data }
        }
        if (responseCategoryPayments.status === 200) {
          categoriesList = !!enabledPayments ? enabledPayments.map((method) => method?.category_association?.fk_id_category_method) : []

          let filteredCategories = responseCategoryPayments?.data?.filter(({ id_category_payment_methods }) =>
            categoriesList.includes(id_category_payment_methods)
          )

          company = { ...company, company_payment_categories: filteredCategories }
        }
      })
      .catch((error) => {
        company = {
          ...company,
          company_payment_categories: [],
          company_payment_methods: [],
          company_address: null,
          company_working_hours: null,
          company_working_periods: null,
        }
        setCompany(company)
        companyRef.current = company
        setLocalItem("company", company, true)
        console.error("ERRO AO BUSCAR DADOS DA COMPANHIA", error)
      })
      .finally(() => {
        setCompany(() => company)
        companyRef.current = company
        setLocalItem("company", company, true)
      })

    const combosSession = getLocalItem("combos", true)
    if (!!combosSession) {
      addMenuOption({
        companyId: company.id_company,
        id: "combos",
        label: "Combos",
        focus: focusMenuOption,
        position: orderItems.length > 0 ? orderItems.find(({ name }) => name === "combos").nro_priority : 1,
        visible: combosSession.length > 0,
      })
      focusMenuOption = false
      setCombos(combosSession)
    }

    const productsSession = getLocalItem("products", true)
    if (!!productsSession) {
      for (const product of productsSession) {
        if (product.flavours.length > 0) {
          addMenuOption({
            companyId: company.id_company,
            id: product.uuid_product,
            label: getProductName(product),
            focus: focusMenuOption,
            position: orderItems.length > 0 ? orderItems.find(({ name }) => name === "produtos").nro_priority : 2,
            visible: true,
          })

          focusMenuOption = false
        }
      }

      setProducts(productsSession)
    }

    const beveragesSession = getLocalItem("beverages", true)
    if (!!beveragesSession) {
      addMenuOption({
        companyId: company.id_company,
        id: "beverages",
        label: "Bebidas",
        focus: focusMenuOption,
        position: orderItems.length > 0 ? orderItems.find(({ name }) => name === "bebidas").nro_priority : 3,
        visible: beveragesSession.length > 0,
      })
      focusMenuOption = false
      setBeverages([...beveragesSession])
    }

    const highlightsSession = getLocalItem("highlights", true)
    if (!!highlightsSession) {
      addMenuOption({
        companyId: company.id_company,
        id: "highlights",
        label: "Destaques",
        focus: focusMenuOption,
        position: orderItems.length > 0 ? orderItems.find(({ name }) => name === "destaques").nro_priority : 0,
        visible: highlightsSession.length > 0,
      })
      focusMenuOption = false
      setHighlights(highlightsSession)
    }

    const schedulesSession = getLocalItem("schedules", true)
    if (!!schedulesSession) {
      setSchedules([...schedulesSession])
    }

    await Promise.all([
      SettingController.fetchAll(),
      FidelityController.fetchAll(),
      PopupController.fetchAll(),
      ComboController.fetchAll({ stWebsite: true }),
      ProductController.fetchAllMemory(),
      BeverageController.fetchAllMemory(),
      HighlightsController.fetchAll(),
      ScheduleController.fetchAll(),
    ])
      .then(
        ([
          responseSettings,
          responseFidelities,
          responsePopups,
          responseCombos,
          responseProducts,
          responseBeverages,
          responseHighlights,
          responseSchedule,
        ]) => {
          if (responseSettings.status === 200) {
            const settings = responseSettings.data
            // Verificando se é preciso exibiir as modais de configuração inicial
            if (startLoad) {
              const companies = getLocalItem("companies", true)
              const userAddress = getLocalItem("userSelectedAddress", true)

              if (formatBoolean(settings.site_get_address_on_early_access)) {
                //Verifica se já existe um endereço no storage
                if (!userAddress) {
                  setModalInitialAddress({ status: true, content: { source: "mainOptions" } })
                } else {
                  //Verifica se está configurado para mostrar a tela de escolha das lojas
                  if (formatBoolean(settings.site_display_company_choose) && companies.length > 1) {
                    if (modalCompanies.status === false) {
                      setModalCompanies({ status: true, content: companies })
                    }
                  }
                }
              } else if (formatBoolean(settings.site_display_company_choose) && companies.length > 1) {
                if (modalCompanies.status === false || userAddress === null) {
                  setModalCompanies({ status: true, content: companies })
                }
              }
            }

            onChangeFavicon(getUrlAWSManagerFile(settings.site_favicon))
            onChangeTitlePage(settings.site_title || company.nm_company || "Restaurante")

            if (!!settings.site_show_order) {
              orderItems = JSON.parse(settings.site_show_order)
              setLocalItem("orderItems", orderItems, true)
            }
            setSettings(settings)
            console.info("CONSULTANDO DADOS DE ENTREGA...")

            // Obtendo informações do pagamento online
            // if (formatBoolean(settings.enable_online_payments)) {
            //   getMercadoPagoPublicKey()
            // }
            setLocalItem("settings", settings, true)
          }

          if (responseFidelities.status === 200) {
            setFidelities(responseFidelities.data)
            for (let fidelity of responseFidelities.data) {
              if (fidelity.nm_fidelity_group) {
                if (!groupFidelitiesRef.current[fidelity.nm_fidelity_group]) {
                  groupFidelitiesRef.current[fidelity.nm_fidelity_group] = [fidelity.id_fidelity]
                } else {
                  groupFidelitiesRef.current[fidelity.nm_fidelity_group].push(fidelity.id_fidelity)
                }
              }
            }

            //Validando se existe
            let fidelityUsageControl = getLocalItem("fidelityUsageControl", true) || {}
            let newFidelityUsageControl = {}
            for (let index = 0; index < Object.keys(fidelityUsageControl).length; index++) {
              const fidelityId = Object.keys(fidelityUsageControl)[index]
              const fidelityUsage = Object.values(fidelityUsageControl)[index]
              const newFidelityUsage = []
              for (const item of fidelityUsage) {
                const currentTime = Date.now()
                const usageTime = typeof item.usedAt === "number" ? item.usedAt : new Date(item.usedAt).getTime()
                const hoursPassed = (currentTime - usageTime) / (1000 * 60 * 60)
                if (hoursPassed < 24) {
                  newFidelityUsage.push(item)
                }
              }
              if (newFidelityUsage.length > 0) {
                newFidelityUsageControl[fidelityId] = newFidelityUsage
              }
            }
            setLocalItem("fidelityUsageControl", newFidelityUsageControl, true)
          }

          if (!!localUser) {
            setUser(localUser)
            userRef.current = localUser

            let user = localUser
            //Caso já tenha usuário logado, busca novamente os dados dele para atualizar quaisquer informações que possam
            //ter sido alteradas como pontos de fideldiade e endereços e no futuro cupons
            CustomerController.fetchOne(localUser.uuid_customer, {
              withOrders: true,
              withStreets: true,
              withMovements: true,
              withFidelities: true,
              localUser: { ...localUser, previousDatabase },
            })
              .then(async (responseCustomer) => {
                if (responseCustomer.status === 200) {
                  if (responseCustomer?.data?.dt_blocked_at) {
                    console.error("Usuário não autorizado, limpando dados do usuário")
                    onClearUserData()
                  } else {
                    const addresses = responseCustomer.data?.streets || []
                    const customer_orders = responseCustomer.data?.customer_orders
                    const fidelities = responseCustomer.data?.customer_fidelities_points || []
                    const movements_account = responseCustomer.data?.movements_account || []
                    const cashbackUsed = getLocalItem("cashbackUsed")
                    if (movements_account.length > 0 && cashbackUsed) {
                      const [dateUsed, val_used] = cashbackUsed.split("__")
                      for (const movement of movements_account) {
                        const { type_movement_customer_account, dt_deleted_at } = movement
                        if (!dt_deleted_at && type_movement_customer_account === "output") {
                          const { dt_created_at } = movement
                          const sameDay = moment(dateUsed).isSame(dt_created_at, "D")
                          const sameMonth = moment(dateUsed).isSame(dt_created_at, "M")
                          const sameYear = moment(dateUsed).isSame(dt_created_at, "Y")
                          if (sameDay && sameMonth && sameYear) {
                            const { val_movement } = movement
                            if (parseFloat(Number.parseFloat(val_used).toFixed(2)) === val_movement) {
                              removeLocalItem("cashbackUsed")
                            }
                          }
                        }
                      }
                    }

                    if (addresses.length > 0) {
                      const defaultAddress =
                        addresses.find(({ customer_address: { st_primary, dt_deleted_at } }) => st_primary && !dt_deleted_at) || addresses[0]

                      if (
                        !userSelectedAddress?.nm_street &&
                        modality !== 2 &&
                        modality !== 3 &&
                        !!companyRef.current?.company_modality?.find(({ fk_id_modality }) => fk_id_modality === 1)
                      ) {
                        setModality(1)
                        setLocalItem("modality", 1)
                        onChangeUserSelectedAddress(defaultAddress).then()
                      }
                    }

                    const newUser = { ...user, customer_orders, addresses, fidelities, movements_account }
                    setUser(newUser)
                    userRef.current = newUser
                    setLocalItem("user", newUser, true)
                  }
                } else {
                  console.error("Usuário não encontrado na base de dados, limpando dados do usuário")
                  onClearUserData()
                }
              })
              .catch((error) => {
                console.error("Falha ao atualizar dados do cliente", error)
                setUser(user)
                userRef.current = user
              })
          }

          if (responsePopups.status === 200) {
            setLocalItem("popups", responsePopups, true)
          }

          const currentDate = new moment()
          if (responseCombos.status === 200 && responseCombos.data.total > 0) {
            const combosFiltered = (responseCombos?.data?.rows || [])
              .filter(({ combo_periods = [] }) => checkBetweenIntervalPeriod(currentDate, combo_periods, "combos_periods_times"))
              .sort((a, b) => a.nro_priority - b.nro_priority)

            if (!combosSession) {
              addMenuOption({
                companyId: company.id_company,
                id: "combos",
                label: "Combos",
                focus: focusMenuOption,
                position: orderItems.length > 0 ? orderItems.find(({ name }) => name === "combos").nro_priority : 1,
                visible: combosFiltered.length > 0,
              })
              focusMenuOption = false
            }

            setCombos(combosFiltered)
            setLocalItem("combos", combosFiltered, true)
          } else {
            setCombos([])
            setLocalItem("combos", [], true)
            removeMenuOption("combos")
          }

          if (responseProducts.status === 200) {
            let newProducts = []
            let menuOptionsItems = []
            const localMenuOptions = getLocalItem("menuOptions", true)
            if (!!localMenuOptions) {
              menuOptionsItems = [...localMenuOptions]
            }
            const fetcheProducts = responseProducts.data.rows.filter(
              ({ company_association: { products_periods, st_period_control, st_website } }) =>
                st_website &&
                (st_period_control
                  ? !!products_periods && products_periods.length === 7
                    ? !!checkBetweenIntervalPeriod(currentDate, products_periods, "products_periods_times")
                    : true
                  : true)
            )
            // tratativa de cada item do estabelecimento
            for (let product of fetcheProducts) {
              let continueLoading = true

              const { id_product, uuid_product, nm_product, company_association } = product
              product = { id_product, uuid_product, nm_product, ...company_association }

              ProductController.fetchSizes(product.uuid_product).then((sizeResponse) => {
                if (sizeResponse.status === 200) {
                  let sizes = []
                  let product = sizeResponse.data
                  const fetchedProductSizes = product.sizes.filter(
                    ({ product_size: { st_website, st_hide_in_builder } }) => st_website && !st_hide_in_builder
                  )
                  if (fetchedProductSizes.length > 0) {
                    // Obtendo os tamanhos do produto
                    for (const size of fetchedProductSizes) {
                      const { id_size, uuid_size, nm_size, product_size, beverages = [] } = size
                      const beveragesSize = beverages.filter(({ company_association: { st_website } }) => st_website)

                      sizes.push({
                        id_size,
                        uuid_size,
                        nm_size,
                        ...product_size,
                        beverages: beveragesSize.map(({ id_beverage, uuid_beverage, nm_beverage, beverage_size, company_association = {} }) => ({
                          ...company_association,
                          id_beverage,
                          uuid_beverage,
                          nm_beverage,
                          val_price: beverage_size.val_price || 0,
                        })),
                      })
                    }

                    // Atualizando o objeto de produto
                    sizes.sort((a, b) => a.nro_priority - b.nro_priority)

                    // Verificando de qual o index do produto em questão que teve retorno.
                    const productIndex = newProducts.findIndex(({ uuid_product }) => uuid_product === product.uuid_product)

                    // Adiciona os dados do tamanho aquele produto.
                    if (productIndex !== -1) {
                      newProducts[productIndex] = { ...newProducts[productIndex], sizes }
                      menuOptionsItems.push({
                        companyId: company.id_company,
                        id: newProducts[productIndex].uuid_product,
                        label: getProductName(newProducts[productIndex]),
                        focus: focusMenuOption,
                        position: orderItems.length > 0 ? orderItems.find(({ name }) => name === "produtos").nro_priority : 2,
                        visible: true,
                      })
                    }
                    const newSortedProducts = newProducts.filter(({ st_website }) => st_website).sort((a, b) => a.nro_priority - b.nro_priority)

                    menuOptionsItems.sort((a, b) => a.nro_priority - b.nro_priority)

                    for (const newSortedProduct of newSortedProducts) {
                      menuOptionsItems.forEach((menuItem) => {
                        if (menuItem.id === newSortedProduct.uuid_product) {
                          addMenuOption(menuItem)
                        }
                      })
                    }

                    setProducts(newSortedProducts)

                    const productSizesStorage = getLocalItem("productSizes", true) || {}

                    setLocalItem("productSizes", { ...productSizesStorage, [product.uuid_product]: sizes }, true)
                    setLocalItem("products", newSortedProducts, true)
                  } else {
                    continueLoading = false
                  }
                }

                if (continueLoading) {
                  if (product.st_display_mode === "flavour") {
                    ProductController.fetchFlavours({
                      uuid_product: product.uuid_product,
                      withSizes: true,
                      withIngredients: false,
                      withOptionals: false,
                    }).then((flavourResponse) => {
                      const productIndex = newProducts.findIndex(({ uuid_product }) => uuid_product === product.uuid_product)

                      if (flavourResponse.status === 200) {
                        let flavours = []
                        let product = flavourResponse.data
                        // Identifica de qual produto é o retorno do sabor.

                        // Obtendo os sabores do produto e adicionando ao flavours.
                        const fetchedFlavours = product.flavours.filter(({ product_flavour: { st_website }, sizes }) => {
                          const sizesEnabled = sizes.filter(({ flavour_size: { st_website } }) => st_website)

                          return st_website && sizesEnabled.length > 0
                        })

                        for (const flavour of fetchedFlavours) {
                          const { id_flavour, uuid_flavour, nm_flavour, product_flavour, sizes } = flavour
                          let sizesEnabled = sizes.filter(({ flavour_size: { st_website } }) => st_website)
                          flavours.push({
                            id_flavour,
                            uuid_flavour,
                            nm_flavour,
                            ...product_flavour,
                            sizes: sizesEnabled,
                          })
                        }
                        // Atualizando o objeto de sabores para os que estão habilitados no site
                        flavours.sort((a, b) => a.nro_priority - b.nro_priority)

                        if (productIndex !== -1) {
                          newProducts[productIndex] = { ...newProducts[productIndex], flavours }
                          if (flavours.length > 0) {
                            menuOptionsItems.push({
                              companyId: company.id_company,
                              id: newProducts[productIndex].uuid_product,
                              label: getProductName(newProducts[productIndex]),
                              focus: focusMenuOption,
                              position: orderItems.length > 0 ? orderItems.find(({ name }) => name === "produtos").nro_priority : 2,
                              visible: true,
                            })
                          }
                        }

                        // Refatorando menu de acordo com produtos disponíveis
                        // Filtrando apenas produtos com sabores
                        const newSortedProducts = newProducts
                          .filter(({ st_display_mode, flavours }) => st_display_mode === "size" || flavours.length > 0)
                          .sort((a, b) => a.nro_priority - b.nro_priority)

                        menuOptionsItems.sort((a, b) => a.nro_priority - b.nro_priority)

                        for (const newSortedProduct of newSortedProducts) {
                          menuOptionsItems.forEach((menuItem) => {
                            if (menuItem.id === newSortedProduct.uuid_product) {
                              addMenuOption(menuItem)
                            }
                          })
                        }

                        // salvando os estados
                        setProducts(newSortedProducts)
                        // salvando os localStorage
                        setLocalItem("products", newSortedProducts, true)
                        const productFlavoursStorage = getLocalItem("productFlavours", true) || {}
                        setLocalItem(
                          "productFlavours",
                          {
                            ...productFlavoursStorage,
                            [product.uuid_product]: flavours,
                          },
                          true
                        )
                      } else if (flavourResponse.status === 204) {
                        // Se o retorno é 204, significa que não possui sabores cadastrados, e é para remover o produto para não mostrar no site.
                        if (productIndex !== -1) {
                          newProducts.splice(productIndex, 1)
                        }
                      }
                    })
                  }
                }
              })

              const productSizesStorage = getLocalItem("productSizes", true) || {}
              const productFlavoursStorage = getLocalItem("productFlavours", true) || {}

              newProducts.push({
                ...product,
                sizes: productSizesStorage[product.uuid_product] || [],
                flavours: productFlavoursStorage[product.uuid_product] || [],
              })
            }

            // Alterando o estado, populando os produtos do cliente
            setProducts(newProducts)
            setLocalItem("products", newProducts, true)
          } else {
            ProductController.fetchAll({ createMemory: true })
              .then((productsResponse) => {
                if (productsResponse.status === 200) {
                  let newProducts = []
                  let menuOptionsItems = []
                  const localMenuOptions = getLocalItem("menuOptions", true)
                  if (!!localMenuOptions) {
                    menuOptionsItems = [...localMenuOptions]
                  }
                  const fetcheProducts = productsResponse.data.rows.filter(
                    ({ company_association: { products_periods, st_period_control, st_website } }) =>
                      st_website &&
                      (st_period_control
                        ? !!products_periods && products_periods.length === 7
                          ? !!checkBetweenIntervalPeriod(currentDate, products_periods, "products_periods_times")
                          : true
                        : true)
                  )
                  // tratativa de cada item do estabelecimento
                  for (let product of fetcheProducts) {
                    let continueLoading = true

                    const { id_product, uuid_product, nm_product, company_association } = product
                    product = { id_product, uuid_product, nm_product, ...company_association }

                    ProductController.fetchSizes(product.uuid_product).then((sizeResponse) => {
                      if (sizeResponse.status === 200) {
                        let sizes = []
                        let product = sizeResponse.data
                        const fetchedProductSizes = product.sizes.filter(({ product_size: { st_website } }) => st_website)

                        if (fetchedProductSizes.length > 0) {
                          // Obtendo os tamanhos do produto
                          for (const size of fetchedProductSizes) {
                            const { id_size, uuid_size, nm_size, product_size, beverages = [] } = size

                            const beveragesSize = beverages.filter(({ company_association: { st_website } }) => st_website)

                            sizes.push({
                              id_size,
                              uuid_size,
                              nm_size,
                              ...product_size,
                              beverages: beveragesSize.map(
                                ({ id_beverage, uuid_beverage, nm_beverage, beverage_size, company_association = {} }) => ({
                                  ...company_association,
                                  id_beverage,
                                  uuid_beverage,
                                  nm_beverage,
                                  val_price: beverage_size.val_price || 0,
                                })
                              ),
                            })
                          }

                          // Atualizando o objeto de produto
                          sizes.sort((a, b) => a.nro_priority - b.nro_priority)

                          // Verificando de qual o index do produto em questão que teve retorno.
                          const productIndex = newProducts.findIndex(({ uuid_product }) => uuid_product === product.uuid_product)

                          // Adiciona os dados do tamanho aquele produto.
                          if (productIndex !== -1) {
                            newProducts[productIndex] = { ...newProducts[productIndex], sizes }
                            menuOptionsItems.push({
                              companyId: company.id_company,
                              id: newProducts[productIndex].uuid_product,
                              label: getProductName(newProducts[productIndex]),
                              focus: focusMenuOption,
                              position: orderItems.length > 0 ? orderItems.find(({ name }) => name === "produtos").nro_priority : 2,
                              visible: true,
                            })
                          }
                          const newSortedProducts = newProducts.filter(({ st_website }) => st_website).sort((a, b) => a.nro_priority - b.nro_priority)

                          menuOptionsItems.sort((a, b) => a.nro_priority - b.nro_priority)

                          for (const newSortedProduct of newSortedProducts) {
                            menuOptionsItems.forEach((menuItem) => {
                              if (menuItem.id === newSortedProduct.uuid_product) {
                                addMenuOption(menuItem)
                              }
                            })
                          }

                          setProducts(newSortedProducts)

                          const productSizesStorage = getLocalItem("productSizes", true) || {}

                          setLocalItem("productSizes", { ...productSizesStorage, [product.uuid_product]: sizes }, true)
                          setLocalItem("products", newSortedProducts, true)
                        } else {
                          continueLoading = false
                        }
                      }

                      if (continueLoading) {
                        if (product.st_display_mode === "flavour") {
                          ProductController.fetchFlavours({
                            uuid_product: product.uuid_product,
                            withSizes: true,
                            withIngredients: false,
                            withOptionals: false,
                          }).then((flavourResponse) => {
                            if (flavourResponse.status === 200) {
                              let flavours = []
                              let product = flavourResponse.data

                              // Identifica de qual produto é o retorno do sabor.
                              const productIndex = newProducts.findIndex(({ uuid_product }) => uuid_product === product.uuid_product)

                              if (flavourResponse.status === 200) {
                                // Obtendo os sabores do produto e adicionando ao flavours.
                                const fetchedFlavours = product.flavours.filter(({ product_flavour: { st_website }, sizes }) => {
                                  const sizesEnabled = sizes.filter(({ flavour_size: { st_website } }) => st_website)
                                  return st_website && sizesEnabled.length > 0
                                })

                                for (const flavour of fetchedFlavours) {
                                  const { id_flavour, uuid_flavour, nm_flavour, product_flavour } = flavour
                                  flavours.push({
                                    id_flavour,
                                    uuid_flavour,
                                    nm_flavour,
                                    ...product_flavour,
                                  })
                                }
                                // Atualizando o objeto de sabores para os que estão habilitados no site
                                flavours.sort((a, b) => a.nro_priority - b.nro_priority)
                              } else if (flavourResponse.status === 204) {
                                // Se o retorno é 204, significa que não possui sabores cadastrados, e é para remover o produto para não mostrar no site.
                                if (productIndex !== -1) {
                                  newProducts.splice(productIndex, 1)
                                }
                              }

                              if (productIndex !== -1) {
                                newProducts[productIndex] = { ...newProducts[productIndex], flavours }
                                if (flavours.length > 0) {
                                  menuOptionsItems.push({
                                    companyId: company.id_company,
                                    id: newProducts[productIndex].uuid_product,
                                    label: getProductName(newProducts[productIndex]),
                                    focus: focusMenuOption,
                                    position: orderItems.length > 0 ? orderItems.find(({ name }) => name === "produtos").nro_priority : 2,
                                    visible: true,
                                  })
                                }
                              }

                              // Refatorando menu de acordo com produtos disponíveis
                              // Filtrando apenas produtos com sabores
                              const newSortedProducts = newProducts
                                .filter(({ st_display_mode, flavours }) => st_display_mode === "size" || flavours.length > 0)
                                .sort((a, b) => a.nro_priority - b.nro_priority)

                              menuOptionsItems.sort((a, b) => a.nro_priority - b.nro_priority)

                              for (const newSortedProduct of newSortedProducts) {
                                menuOptionsItems.forEach((menuItem) => {
                                  if (menuItem.id === newSortedProduct.uuid_product) {
                                    addMenuOption(menuItem)
                                  }
                                })
                              }

                              // salvando os estados
                              setProducts(newSortedProducts)
                              // salvando os localStorage
                              setLocalItem("products", newSortedProducts, true)
                              const productFlavoursStorage = getLocalItem("productFlavours", true) || {}
                              setLocalItem(
                                "productFlavours",
                                {
                                  ...productFlavoursStorage,
                                  [product.uuid_product]: flavours,
                                },
                                true
                              )
                            }
                          })
                        }
                      }
                    })

                    const productSizesStorage = getLocalItem("productSizes", true) || {}
                    const productFlavoursStorage = getLocalItem("productFlavours", true) || {}

                    newProducts.push({
                      ...product,
                      sizes: productSizesStorage[product.uuid_product] || [],
                      flavours: productFlavoursStorage[product.uuid_product] || [],
                    })
                  }

                  // Alterando o estado, populando os produtos do cliente
                  setProducts(newProducts)
                  setLocalItem("products", newProducts, true)
                }
              })
              .catch((error) => {
                console.error("Falha ao consultar produtos no banco", error)
              })
          }

          removeMenuOption("products")

          if (responseBeverages.status === 200 && responseBeverages.data.total > 0) {
            const { rows: beverages } = responseBeverages.data
            addMenuOption({
              companyId: company.id_company,
              id: "beverages",
              label: "Bebidas",
              focus: focusMenuOption,
              position: orderItems.length > 0 ? orderItems.find(({ name }) => name === "bebidas").nro_priority : 3,
              visible: beverages.length > 0,
            })
            focusMenuOption = false

            // Verificando se existe a opção sem bebida
            const beveragesFiltered = beverages
              .filter(
                ({ nm_beverage, company_association }) =>
                  `${nm_beverage}`.toLowerCase().indexOf("sem b") === -1 &&
                  `${nm_beverage}`.toLowerCase().indexOf("sem r") === -1 &&
                  company_association.st_website &&
                  (!!company_association.beverages_periods && company_association.beverages_periods.length === 7
                    ? !!checkBetweenIntervalPeriod(currentDate, company_association.beverages_periods, "beverages_periods_times")
                    : true)
              )
              .map(({ id_beverage, uuid_beverage, nm_beverage, ref_beverage, category, company_association }) => {
                return {
                  id_beverage,
                  uuid_beverage,
                  nm_beverage,
                  ref_beverage,
                  category,
                  ...company_association,
                }
              })
              .sort((a, b) => a.nro_priority - b.nro_priority)
            setBeverages(beveragesFiltered)
            setLocalItem("beverages", beveragesFiltered, true)
          } else {
            BeverageController.fetchAll({ createMemory: true }).then((responseBeverages) => {
              if (responseBeverages.status === 200 && responseBeverages.data.total > 0) {
                const { rows: beverages } = responseBeverages.data
                addMenuOption({
                  companyId: company.id_company,
                  id: "beverages",
                  label: "Bebidas",
                  focus: focusMenuOption,
                  position: orderItems.length > 0 ? orderItems.find(({ name }) => name === "bebidas").nro_priority : 3,
                  visible: beverages.length > 0,
                })
                focusMenuOption = false

                // Verificando se existe a opção sem bebida
                const beveragesFiltered = beverages
                  .filter(
                    ({ nm_beverage, company_association }) =>
                      `${nm_beverage}`.toLowerCase().indexOf("sem ") === -1 &&
                      company_association.st_website &&
                      (!!company_association.beverages_periods && company_association.beverages_periods.length === 7
                        ? !!checkBetweenIntervalPeriod(currentDate, company_association.beverages_periods, "beverages_periods_times")
                        : true)
                  )
                  .map(({ id_beverage, uuid_beverage, nm_beverage, ref_beverage, category, company_association }) => {
                    return {
                      id_beverage,
                      uuid_beverage,
                      nm_beverage,
                      ref_beverage,
                      category,
                      ...company_association,
                    }
                  })
                  .sort((a, b) => a.nro_priority - b.nro_priority)
                setBeverages(beveragesFiltered)
                setLocalItem("beverages", beveragesFiltered, true)
              } else {
                setBeverages([])
                setLocalItem("beverages", [], true)
                removeMenuOption("beverages")
              }
            })
          }

          if (responseHighlights.status === 200 && responseHighlights.data.total > 0) {
            if (!highlightsSession) {
              addMenuOption({
                companyId: company.id_company,
                id: "highlights",
                label: "Destaques",
                focus: focusMenuOption,
                position: orderItems.length > 0 ? orderItems.find(({ name }) => name === "destaques").nro_priority : 0,
                visible: responseHighlights.data.total > 0,
              })
              focusMenuOption = false
            }

            setHighlights(responseHighlights.data.rows)
            setLocalItem("highlights", responseHighlights.data.rows, true)
          } else {
            setHighlights([])
            setLocalItem("highlights", [], true)
            removeMenuOption("destaques")
          }

          if (responseSchedule.status === 200) {
            setSchedules(responseSchedule.data)
            setLocalItem("schedules", responseSchedule.data, true)
          }
        }
      )
      .catch((error) => {
        console.error(error)
        setLoading(false)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  //Atualizar dados do endereço do cliente
  const onChangeUserSelectedAddress = async (address = null, finishOrder = false, currentSettings) => {
    let newAddress = { ...address }
    const newSettings = !!currentSettings ? currentSettings : settings
    if (!!newSettings || (Array.isArray(newSettings) && newSettings.length > 0)) {
      try {
        let deliveryFee = 0
        let deliveryTime = 0

        const responseDeliveryCalculate = await DeliveryFeeController.calculate(
          address.customer_address.geo_coordinates[0],
          address.customer_address.geo_coordinates[1],
          address.neighbourhood.id_neighbourhood,
          1,
          address?.customer_address?.id_customer_address
        )

        switch (responseDeliveryCalculate.status) {
          case 200:
            deliveryFee = parseFloat(responseDeliveryCalculate.data.val_fee.toFixed(2))
            deliveryTime = responseDeliveryCalculate.data.num_delivery_time

            if (!!order.payments) {
              const { discountType, discount } = getDiscountType(1, false)
              const discountOrder = getDiscountOrder({ ...order, discountType, discount }, order.subTotal, deliveryFee, settings, 1)
              const orderPayment = order.payments.find(({ nm_payment_method }) => nm_payment_method.toLowerCase() !== "cashback")
              const cashBackPayment = order.payments.find(({ nm_payment_method }) => nm_payment_method.toLowerCase() === "cashback")

              if (!!orderPayment) {
                if (
                  orderPayment.val_payment !== order.subTotal + deliveryFee - discountOrder - (!!cashBackPayment ? cashBackPayment.val_payment : 0) ||
                  orderPayment.id_payment_method === 1
                ) {
                  if (
                    orderPayment.id_payment_method !== 1 ||
                    (orderPayment.id_payment_method === 1 &&
                      (orderPayment.val_receivable >
                        order.subTotal + deliveryFee - discountOrder - (!!cashBackPayment ? cashBackPayment.val_payment : 0) ||
                        orderPayment.val_receivable === 0))
                  ) {
                    let payments = [
                      {
                        ...orderPayment,
                        val_payment: order.subTotal + deliveryFee - discountOrder - (!!cashBackPayment ? cashBackPayment.val_payment : 0),
                      },
                    ]
                    if (!!cashBackPayment) {
                      payments.push(cashBackPayment)
                    }

                    setOrder({
                      ...order,
                      discountType,
                      discount,
                      payments,
                    })
                  } else {
                    setOrder({ ...order, discountType, discount, payments: [] })
                    setSelectedPaymentMethod({
                      id_payment_method: -1,
                    })
                    setShowPaymentTypeSelector(true)
                    onMessageDialog()
                      .fire({
                        title: "Devido a alteração no valor do pedido, informe o novo valor de troco!",
                        icon: "info",
                        timer: 3000,
                        showConfirmButton: false,
                        showCancelButton: false,
                      })
                      .then()
                  }
                }
              }
            }

            setUserSelectedAddress({ ...address, deliveryTime, deliveryFee })
            setLocalItem("userSelectedAddress", { ...address, deliveryTime, deliveryFee }, true)
            userSelectedAddressRef.current = { ...address, deliveryTime, deliveryFee }
            newAddress = { ...address, deliveryTime, deliveryFee }
            return newAddress
          case 204:
            onMessageDialog("error")
              .fire({
                title: `Infelizmente não entregamos no seu endereço ${companies.length > 1 ? ",verifique nas demais lojas" : ""}.`,
                icon: "warning",
                timer: 3000,
                showConfirmButton: false,
                showCancelButton: false,
              })
              .then()

            if (companies.length > 1 && modalCompanies.status === false) {
              setModalCompanies({ status: true, content: companies })
            }
            return false
          default:
            onMessageDialog("error")
              .fire({
                title: "Ocorreu um erro ao consultar a taxa de entrega, entre em contato com o estabelecimento!",
                icon: "warning",
                timer: 3000,
                showConfirmButton: false,
                showCancelButton: false,
              })
              .then()
            return false
        }
        // })
      } catch (error) {
        console.info(error)
        return false
      }
    }
  }

  const getDeliveryFees = (settings, companyUid = null) => {
    deliveryFeesRef.current = []
  }

  const getDeliveryAreas = () => {
    return DeliveryAreasController.fetchAll().then((responseAreas) => {
      if (responseAreas.status === 200) {
        return responseAreas.data
      }
    })
  }

  const getStartHour = (workingHour) => {
    const startHour = workingHour.start_hour.toString().length === 1 ? "0" + workingHour.start_hour.toString() : workingHour.start_hour.toString()
    const startMinute =
      workingHour.start_minute.toString().length === 1 ? "0" + workingHour.start_minute.toString() : workingHour.start_minute.toString()
    return startHour + ":" + startMinute + ":00"
  }

  const getEndHour = (workingHour) => {
    const endHour = workingHour.end_hour.toString().length === 1 ? "0" + workingHour.end_hour.toString() : workingHour.end_hour.toString()
    const endMinute = workingHour.end_minute.toString().length === 1 ? "0" + workingHour.end_minute.toString() : workingHour.end_minute.toString()
    return endHour + ":" + endMinute + ":00"
  }

  const getSecondsOfHour = (hour) => {
    return hour.split(":").reduce((acc, time) => 60 * acc + +time)
  }

  const getWorkingHour = (date) => {
    try {
      if (!!companyRef.current && !!companyRef.current.company_working_hours) {
        let currentDate = new moment()
        let currentSecondsHour = getSecondsOfHour(getFormatDate(currentDate).getHour("HH:mm:ss"))
        const startHour = getStartHour(companyRef.current.company_working_hours)
        const startHourSeconds = getSecondsOfHour(startHour)
        const endHour = getEndHour(companyRef.current.company_working_hours)
        const endHourSeconds = getSecondsOfHour(endHour)
        let endDate = new moment()
        let startDate = new moment()

        if (!!date) {
          currentDate = new moment(date)
          currentSecondsHour = getSecondsOfHour(getFormatDate(currentDate).getHour("HH:mm:ss"))
        }
        if (startHourSeconds < currentSecondsHour) {
          startDate = moment(currentDate.format("YYYY-MM-DD " + startHour))
          if (endHourSeconds < currentSecondsHour && endHourSeconds < startHourSeconds) {
            endDate = moment(
              moment(currentDate)
                .add(1, "days")
                .format("YYYY-MM-DD " + endHour)
            )
          } else {
            endDate = moment(moment(currentDate).format("YYYY-MM-DD " + endHour))
          }
        } else {
          // if (currentSecondsHour < startHourSeconds && currentSecondsHour > endHourSeconds) {
          //   startDate = moment(currentDate.format("YYYY-MM-DD " + startHour))
          // } else {
          startDate = moment(
            moment(currentDate)
              .subtract(1, "days")
              .format("YYYY-MM-DD " + startHour)
          )
          // }
          if (endHourSeconds > currentSecondsHour && endHourSeconds > startHourSeconds) {
            endDate = moment(
              moment(currentDate)
                .add(1, "days")
                .format("YYYY-MM-DD " + endHour)
            )
          } else {
            endDate = moment(moment(currentDate).format("YYYY-MM-DD " + endHour))
          }
        }

        return { startDate, endDate }
      } else {
        return { startDate: null, endDate: null }
      }
    } catch (error) {
      const currentDate = new moment()
      const startDate = moment(moment(currentDate).format("YYYY-MM-DD " + "00:00:00"))
      const endDate = moment(
        moment(currentDate)
          .add(1, "days")
          .format("YYYY-MM-DD " + "04:00:00")
      )
      return { startDate, endDate }
    }
  }

  const getMercadoPagoPublicKey = async () => {
    await loadMercadoPago()

    setLoading(true)

    const publicKeyRequest = await MercadoPagoController.getPublicKey()

    if (publicKeyRequest.status === 200) {
      const mp = new window.MercadoPago(publicKeyRequest.data)

      setMercadoPagoInstance(mp)
    }

    setLoading(false)
  }

  const showPopupMessage = (popupData, popupStatus) => {
    if (!!popupData) {
      if (!!popupStatus) {
        if (popupStatus[popupData.uuid_popup] === true) {
          setPopup(popupData)
        } else if (popupStatus[popupData.uuid_popup] === false) {
          //
        } else {
          setPopup(popupData)
        }
      } else {
        setPopup(popupData)
      }
    }
  }

  //Função para logar no sistema
  const onSignIn = async (username, password = null, loginWhatsapp = false) => {
    setLoading(true)

    CustomerController.signIn(encrypt(username, "base64"), encrypt(password, "base64"), loginWhatsapp)
      .then((response) => {
        if (response.status === 200) {
          setModalSignIn({ status: false })
          const user = response.data

          // setAddressesLoading(true)
          setUser({ ...user, addresses: [], fidelities: [] })
          userRef.current = { ...user, addresses: [], fidelities: [] }
          setLocalItem("user", { ...user, addresses: [], fidelities: [], movements_account: [] }, true)

          CustomerController.fetchOne(user.uuid_customer, {
            withOrders: true,
            withStreets: true,
            withMovements: true,
            withFidelities: true,
            withContacts: true,
          }).then(async (responseCustomer) => {
            if (responseCustomer.status === 200) {
              const addresses = responseCustomer.data?.streets || []
              const fidelities = responseCustomer.data?.customer_fidelities_points || []
              const movements_account = responseCustomer.data?.movements_account || []

              const cashbackUsed = getLocalItem("cashbackUsed")

              if (movements_account.length > 0 && cashbackUsed) {
                const [dateUsed, val_used] = cashbackUsed.split("__")
                for (const movement of movements_account) {
                  const { type_movement_customer_account, dt_deleted_at } = movement
                  if (!dt_deleted_at && type_movement_customer_account === "output") {
                    const { dt_created_at } = movement
                    const sameDay = moment(dateUsed).isSame(dt_created_at, "D")
                    const sameMonth = moment(dateUsed).isSame(dt_created_at, "M")
                    const sameYear = moment(dateUsed).isSame(dt_created_at, "Y")
                    if (sameDay && sameMonth && sameYear) {
                      const { val_movement } = movement
                      if (parseFloat(Number.parseFloat(val_used).toFixed(2)) === val_movement) {
                        removeLocalItem("cashbackUsed")
                      }
                    }
                  }
                }
              }
              const dt_birth = responseCustomer.data?.dt_birth || ""
              const customer_orders = responseCustomer.data?.customer_orders

              if (addresses.length > 0) {
                const defaultAddress =
                  addresses.find(({ customer_address: { st_primary, dt_deleted_at } }) => st_primary && !dt_deleted_at) || addresses[0]
                if (
                  !userSelectedAddress?.nm_street &&
                  modality !== 2 &&
                  modality !== 3 &&
                  !!companyRef.current?.company_modality?.find(({ fk_id_modality }) => fk_id_modality === 1)
                ) {
                  setModality(1)
                  setLocalItem("modality", 1)
                  onChangeUserSelectedAddress(defaultAddress).then()
                }
              }

              setUser({ ...user, customer_orders, addresses, dt_birth, fidelities, movements_account })
              userRef.current = { ...user, customer_orders, addresses, dt_birth, fidelities, movements_account }
              setLocalItem("user", { ...user, customer_orders, addresses, fidelities, movements_account }, true)
            }
          })
        } else if (response.status === 403) {
          onMessageDialog("warning")
            .fire({
              title: `Login não autorizado, entre em contato com a loja!`,
              icon: "error",
              timer: 5000,
              showCancelButton: false,
              showConfirmButton: false,
            })
            .then()
        } else {
          if (!loginWhatsapp) {
            onMessageDialog("success")
              .fire({
                title: `Usuário ou senha incorretos! 😟`,
                icon: "error",
                timer: 2000,
                showCancelButton: false,
                showConfirmButton: false,
              })
              .then()
          }
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const onChangeStatusCompany = (st_website) => {
    const storageCompany = getLocalItem("company", true)

    setCompany({ ...storageCompany, st_website })
    companyRef.current = { ...storageCompany, st_website }
    setLocalItem("company", { ...storageCompany, st_website }, true)
  }

  const onChangeOrder = (content) => {
    setSessionItem("order", content, true)
    const [total, subTotal, benefit] = getTotalOrder(content, userSelectedAddressRef.current?.deliveryFee)
    setOrder({
      ...order,
      ...content,
      total,
      subTotal: subTotal + benefit,
    })
  }

  const onChangeFavicon = (link) => {
    let favicon = document.querySelector('link[rel="icon"]')
    if (favicon !== null) {
      favicon.href = link
    } else {
      favicon = document.createElement("link")
      favicon.rel = "icon"
      favicon.href = link
      document.head.appendChild(favicon)
    }
  }

  const onChangeTitlePage = (title) => {
    document.title = title
  }

  const onPlaceSelected = async (place, feature) => {
    setLoading(true)
    if (!!place) {
      const addressInfo = place.coordinates || false

      if (addressInfo === false) {
        setLoading(false)
        onMessageDialog()
          .fire({
            title: "Não conseguimos identificar a sua localização, tente novamente!",
            icon: "info",
            timer: 3000,
            showConfirmButton: false,
            showCancelButton: false,
          })
          .then()
      } else {
        const { latitude, longitude } = addressInfo

        setModalMinimapAddress({ status: true, content: { latitude, longitude, feature } })
        setLoading(false)
      }
    } else {
      setLoading(false)
      onMessageDialog()
        .fire({
          title: "Não conseguimos identificar o endereço selecionado, tente novamente!",
          icon: "info",
          timer: 3000,
          showConfirmButton: false,
          showCancelButton: false,
        })
        .then()
    }
  }

  const onConfirmLocation = async (addressDetails) => {
    if (!!location) {
      try {
        if (!!addressDetails) {
          await onUpdateContentFullAddress(addressDetails)
        } else {
          setModalClientAddressNotFound({ status: true, address: { latitude, longitude } })
        }
      } catch (error) {
        console.error("Erro ao buscar endereço:", error)
      } finally {
        setLoading(false)
      }
    } else {
      setLoading(false)
      onMessageDialog()
        .fire({
          title: "Não conseguimos obter a sua localização, tente novamente!",
          icon: "info",
          timer: 3000,
          showConfirmButton: false,
          showCancelButton: false,
        })
        .then()
    }
  }

  const onConfirmNumber = (number, address) => {
    const addressUpdated = {
      ...address,
      customer_address: { ...address.customer_address, num_address: number },
    }

    let editNeighbourhood = false

    if (addressUpdated.neighbourhood.nm_neighbourhood === addressUpdated.neighbourhood.city.nm_city) {
      editNeighbourhood = true

      onMessageDialog()
        .fire({
          title: `Detectamos que o nome do seu bairro é igual ao nome da sua cidade, favor alterar o bairro.`,
          icon: "warning",
          timer: 0,
          showCancelButton: false,
          showConfirmButton: true,
        })
        .then()
    }

    setModalFinalAddress({ status: true, address: addressUpdated, editNeighbourhood })
  }

  const onConfirmAddress = async (addressConfirmed) => {
    const sourceAddress = modalInitialAddress?.content?.source
    switch (sourceAddress) {
      case "mainOptions":
        setModality(1)
        break
      case "orderFinisher":
        setModality(1)
        break
      case "orderPreview":
        setModality(1)
        break
      case "newAddress":
        onUpdateCustomerAddress({ ...addressConfirmed })
        break
      default:
        if (!!editAddressRef.current && editAddressRef.current?.source === "addressesInfoOptions") {
          onUpdateCustomerAddress({ ...addressConfirmed })
          setModality(1)
        }
        break
    }
    setModalInitialAddress({ status: false, content: {} })
    setModalMapAddress({ status: false, location: {} })
    setModalNumberAddress({ status: false, address: {} })
    setModalFinalAddress({ status: false, address: {}, editNeighbourhood: false })
    await onChangeUserSelectedAddress({ ...addressConfirmed })
  }

  // Função para atualizar o conteúdo de um endereço inteiro
  const onUpdateContentFullAddress = async (response) => {
    console.info("CHEGOU NA UPDATE CONTENT FULL ADDRESS")
    setLoading(true)
    try {
      let { street = null, neighbourhood = null, zipcode, city = null, state = null, country, coordinates, number, address } = response

      if (!street && !neighbourhood && !city && !state && !!address) {
        street = address?.nm_street
        state = address?.neighbourhood?.city?.state?.nm_state
        city = address?.neighbourhood?.city?.nm_city
        neighbourhood = address?.neighbourhood?.nm_neighbourhood
      }

      let states = []

      if (states.length === 0) {
        const resultStates = await getStates()
        if (resultStates) {
          setStates(resultStates)
          setLocalItem("states", resultStates, true)
          states = resultStates
        }
      }

      // Os dois estados são alimentados ao montar o componente.
      if (!!states) {
        const [nm_street, id_street_type, nm_type] = await getStreetName(street)
        const [id_state, uuid_state, nm_state, ds_acronym] = getState(states, state)

        if (!!uuid_state) {
          const [id_city, uuid_city, nm_city] = await getCity(city, uuid_state)

          if (!!uuid_city) {
            const [id_neighbourhood, uuid_neighbourhood, nm_neighbourhood, neighbourhoodList] = await getNeighbourhood(neighbourhood, uuid_city)

            let editNeighbourhood = false
            // Verificando se endereço é atendido pelo estabelecimento
            const location = coordinates || {}

            const deliveryFeeType = settings?.display_mode_fee

            const address = {
              zipcode,
              number,
              id_street: null,
              nm_street,
              street_type: { id_street_type, nm_type },
              neighbourhood: {
                id_neighbourhood,
                uuid_neighbourhood,
                nm_neighbourhood,
                city: {
                  id_city,
                  uuid_city,
                  nm_city,
                  state: {
                    id_state,
                    uuid_state,
                    nm_state,
                    ds_acronym,
                  },
                },
              },
              customer_address: {
                geo_coordinates: [location.latitude, location.longitude],
                num_address: editAddressRef.current?.num_address || number || "",
                ds_address_additional_info: editAddressRef.current?.ds_address_additional_info || "",
                ds_observation: editAddressRef.current?.ds_observation || "",
                st_primary: true,
                ds_title: editAddressRef.current?.ds_title || "",
              },
            }

            if (nm_neighbourhood === nm_city) {
              editNeighbourhood = true
            }

            if (deliveryFeeType === "neighborhood" && editNeighbourhood) {
              delete address.neighbourhood.uuid_neighbourhood
              delete address.neighbourhood.id_neighbourhood
            } else {
              const formatedNeighbourhoodList = neighbourhoodList.map((neighbourhood) => ({
                ...neighbourhood,
                city: { id_city, uuid_city, nm_city, state: { id_state, uuid_state, nm_state, ds_acronym } },
              }))
              setNeighbourhoods(formatedNeighbourhoodList)
            }

            setModalFinalAddress({ status: true, address, editNeighbourhood })
          } else {
            onMessageDialog("info")
              .fire({
                title: "Não conseguimos identificar sua cidade, tente novamente!",
                icon: "info",
                timer: 3000,
                showConfirmButton: false,
                showCancelButton: false,
              })
              .then()
          }
        } else {
          onMessageDialog()
            .fire({
              title: "Não conseguimos identificar seu estado, tente novamente!",
              icon: "info",
              timer: 3000,
              showConfirmButton: false,
              showCancelButton: false,
            })
            .then()
        }
      }
    } catch (error) {
      onMessageDialog("info")
        .fire({
          title: "Não conseguimos identificar seu endereço, tente novamente!",
          icon: "info",
          timer: 3000,
          showConfirmButton: false,
          showCancelButton: false,
        })
        .then()
      console.error("error", error)
    } finally {
      setLoading(false)
    }
  }

  // Funcão para verificar a entraga das outras lojas do assinante
  const verifySubscriberDeliveryArea = async (address, location) => {
    //Caso não seja realizada entrega no endereço do cliente, verifica a área de entrega das demais lojas do assinante para redirecionar.
    let otherCompanyDeliveryFees = []
    let subscriberCompanies = []
    for (const otherCompany of companies) {
      if (otherCompany.id_company !== company.id_company) {
        otherCompanyDeliveryFees = await getDeliveryFees(settings, otherCompany.uuid_company)

        let onDeliveryArea = null
        const deliveryFeeType = settings?.display_mode_fee

        if (deliveryFeeType === "neighborhood") {
          onDeliveryArea = !!otherCompanyDeliveryFees
            ? otherCompanyDeliveryFees.find(({ neighbourhood }) => neighbourhood.uuid_neighbourhood === address.neighbourhood.uuid_neighbourhood)
            : null
        } else {
          const companyCoordinates =
            !!company.company_address && company.company_address.length > 0
              ? {
                  latitude: company.company_address[0].geo_coordinates[0],
                  longitude: company.company_address[0].geo_coordinates[1],
                }
              : { latitude: -27.582736076873427, longitude: -48.61364576177011 }

          const customerCoordinates = { latitude: location?.lat, longitude: location?.lng }

          const distance = getDistanceCoordinates(companyCoordinates, customerCoordinates)
          onDeliveryArea = otherCompanyDeliveryFees.find((deliveryFee) => deliveryFee.nro_km_fee >= Math.round(distance))
        }

        if (!!onDeliveryArea || (deliveryFeeType === "neighborhood" && !address.neighbourhood.nm_neighbourhood)) {
          // setModalNumberAddress({ status: true, address })
          subscriberCompanies.push(otherCompany)
        }
      }
    }

    return subscriberCompanies
  }

  const onUpdateCustomerAddress = (address) => {
    setAddressesLoading(true)

    let addresses = [...user.addresses]
    if (!!editAddressRef.current && editAddressRef.current?.source === "addressesInfoOptions") {
      addresses = user.addresses.filter(
        ({ customer_address }) => customer_address?.id_customer_address !== editAddressRef.current?.customer_address?.id_customer_address
      )
      editAddressRef.current = null
    }

    const originalListAddresses = addresses.map((address) => {
      return { ...address, st_primary: false, deleted: true }
    })

    CustomerController.update(user.uuid_customer, {
      addresses: [
        ...originalListAddresses,
        {
          ...address,
          st_primary: true,
        },
      ],
    })
      .then((responseAddressUpdate) => {
        if (responseAddressUpdate.status === 200) {
          const addresses = responseAddressUpdate.data?.streets || []
          if (addresses.length > 0) {
            const defaultAddress = addresses.find(({ st_primary, dt_deleted_at }) => st_primary && !dt_deleted_at) || addresses[0]
            // if (!userSelectedAddress.nm_street) {
            onChangeUserSelectedAddress(defaultAddress).then()
            // }
          }

          setUser({ ...user, addresses })
          userRef.current = { ...user, addresses }
          setLocalItem("user", { ...user, addresses }, true)

          onMessageDialog("success")
            .fire({
              title: "Endereços atualizados com sucesso!",
              icon: "success",
              timer: 3000,
              showCancelButton: false,
              showConfirmButton: false,
            })
            .then()
        } else {
          onMessageDialog("danger")
            .fire({
              title: "Não conseguimos atualizar os seus endereços, tente novamente!",
              icon: "error",
              timer: 3000,
              showCancelButton: false,
              showConfirmButton: true,
              confirmButtonText: "Confirmar",
              reverseButtons: true,
            })
            .then()
        }
      })
      .catch(() => {
        onMessageDialog("danger")
          .fire({
            title: "Não conseguimos atualizar os seus endereços, tente novamente!",
            icon: "error",
            timer: 3000,
            showCancelButton: false,
            showConfirmButton: true,
            confirmButtonText: "Confirmar",
            reverseButtons: true,
          })
          .then()
      })
      .finally(() => {
        setAddressesLoading(false)
      })
  }

  const onConfirmOrderSchedule = (scheduleInfo) => {
    scheduleInfoRef.current = scheduleInfo
    setModalOrderFinisher(true)
  }

  const clearOrder = () => {
    setOrder({
      customer: null,
      combos: [],
      products: [],
      beverages: [],
      payments: [],
      benefits: [],
      total: 0.0,
      subTotal: 0.0,
      comments: "",
      discount: order?.discount || "0,00",
      discountType: order?.discountType || "R$",
      deliveryFee: 0,
      deliveryComments: "",
    })
    scheduleInfoRef.current = null
    finalizingOrderRef.current = false

    setModalOrderPreview(false)
    setModalOrderSchedule(false)
    setModalOrderFinisher(false)
    setModalPaymentMethods({ status: false, content: null })

    setFinalizingOrder(false)
    removeSessionItem("order")
  }

  const preFinishOrder = async (scheduleInfo = null) => {
    if (finalizingOrderRef.current === false) {
      finalizingOrderRef.current = true
      setFinalizingOrder(true)
      setModalOrderProcess({ status: true, resend: false, newRequest: false, scheduleInfo })

      const paymentType = order.payments[0].id_payment_method === 38 ? "pix" : order.payments[0].id_payment_method === 37 ? "creditcard" : null

      if (!!scheduleInfo) {
        finalizingOrderRef.current = false
        setFinalizingOrder(false)
        return finishOrder("schedule", scheduleInfo, false, [37, 38].includes(order.payments[0].id_payment_method), paymentType)
      }

      let { startDate, endDate } = await getWorkingHour()
      if (!!startDate && !!endDate) {
        // Verificando se o cliente já possui um pedido no dia de corrente
        OrderController.countOrdersByCustomer(user.uuid_customer, endDate.format("YYYY-MM-DD HH:mm:ss"), startDate.format("YYYY-MM-DD HH:mm:ss"))
          .then((response) => {
            if (response.status === 200) {
              if (response.data > 0) {
                finalizingOrderRef.current = false
                setFinalizingOrder(false)
                setModalOrderProcess({
                  status: true,
                  resend: true,
                  newRequest: true,
                  scheduleInfo,
                  content: {
                    onlinePayment: [37, 38].includes(order.payments[0].id_payment_method),
                    typeOnlinePayment: paymentType,
                  },
                })
              } else {
                finalizingOrderRef.current = false
                setFinalizingOrder(false)
                finishOrder("direct", null, false, [37, 38].includes(order.payments[0].id_payment_method), paymentType)
              }
            } else {
              onMessageDialog("danger")
                .fire({
                  title: response?.data?.message || "Ocorreu um erro ao processar o pedido, tente novamente!",
                  icon: "error",
                  timer: 3000,
                  showCancelButton: false,
                  showConfirmButton: false,
                  reverseButtons: true,
                })
                .then()
              finalizingOrderRef.current = false
              setFinalizingOrder(false)
              setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
            }
          })
          .catch((error) => {
            console.error("error", error)
            onMessageDialog("danger")
              .fire({
                title: "Ocorreu uma falha ao processar o pedido, tente novamente!",
                icon: "error",
                timer: 3000,
                showCancelButton: false,
                showConfirmButton: true,
                confirmButtonText: "Confirmar",
                reverseButtons: true,
              })
              .then()
            finalizingOrderRef.current = false
            setFinalizingOrder(false)
            setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
          })
      } else {
        onMessageDialog("danger")
          .fire({
            title: "Erro ao processar o pedido, por favor, tente novamente!",
            icon: "error",
            timer: 3000,
            showCancelButton: false,
            showConfirmButton: false,
            reverseButtons: true,
          })
          .then()
        finalizingOrderRef.current = false
        setFinalizingOrder(false)
        setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
      }
    }
  }

  //Função para finalizar o pedido
  const finishOrder = async (
    orderType = "direct",
    scheduleInfo = null,
    st_resended = false,
    onlinePayment = false,
    typeOnlinePayment = null,
    newRequest = false
  ) => {
    if (finalizingOrderRef.current === false) {
      finalizingOrderRef.current = true
      setFinalizingOrder(true)

      setModalOrderProcess({ status: true, resend: false, newRequest: false, scheduleInfo })

      if (modality !== 2 && modality !== 3) {
        if (!userSelectedAddressRef.current) {
          SupportController.throwNewLog({
            type: "warning",
            content: { customer: user },
            message: "Pedido não enviado, faltou informar o endereço",
          }).then()
          setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })

          onMessageDialog()
            .fire({
              title: "Estamos quase lá, só faltou informar o seu endereço...",
              icon: "info",
              timer: 4000,
              showCancelButton: false,
              showConfirmButton: false,
            })
            .then()
          finalizingOrderRef.current = false
          setFinalizingOrder(false)

          return false
        }

        //Verificando se entrega na área selecionada.
        let onDeliveryArea = null
        await DeliveryFeeController.calculate(
          userSelectedAddressRef.current.customer_address.geo_coordinates[0],
          userSelectedAddressRef.current.customer_address.geo_coordinates[1],
          userSelectedAddressRef.current.neighbourhood.id_neighbourhood,
          modality,
          userSelectedAddressRef.current.customer_address.id_customer_address
        ).then((response) => {
          switch (response.status) {
            case 200:
              onDeliveryArea = response.data
              break
            case 204:
              onMessageDialog("warning")
                .fire({
                  title: "Infelizmente não entregamos nessa localização!",
                  icon: "warning",
                  timer: 3000,
                  showConfirmButton: false,
                  showCancelButton: false,
                })
                .then()
              break
            default:
              onMessageDialog("error")
                .fire({
                  title: "Ocorreu um erro ao consultar a taxa de entrega, entre em contato com o estabelecimento!",
                  icon: "warning",
                  timer: 3000,
                  showConfirmButton: false,
                  showCancelButton: false,
                })
                .then()
          }
        })
        if (!onDeliveryArea) {
          SupportController.throwNewLog({
            type: "warning",
            content: { customer: user, deliveryFeesRef: deliveryFeesRef.current },
            message: "Pedido não enviado, endereço não atendido",
          }).then()
          setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })

          onMessageDialog()
            .fire({
              title: "Infelizmente não atendemos no seu endereço! 😕",
              icon: "warning",
              timer: 4000,
              showCancelButton: false,
              showConfirmButton: false,
            })
            .then()
          finalizingOrderRef.current = false
          setFinalizingOrder(false)

          return false
        }

        if (
          !!userSelectedAddressRef.current &&
          (!userSelectedAddressRef.current?.customer_address?.geo_coordinates[0] ||
            !userSelectedAddressRef.current?.customer_address?.geo_coordinates[1])
        ) {
          SupportController.throwNewLog({
            type: "warning",
            content: { customer: user, userSelectedAddressRef: userSelectedAddressRef.current },
            message: "Pedido não enviado, endereço não confirmado(sem coordenadas)",
          }).then()

          onMessageDialog("info")
            .fire({
              title: `Para prosseguir é necessário confirmar seu endereço!`,
              icon: "info",
              confirmButtonText: "Confirmar",
              showConfirmButton: true,
              reverseButtons: true,
              showCancelButton: false,
            })
            .then(async ({ isConfirmed }) => {
              if (isConfirmed) {
                setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
                const addressSuggestionsByName = await getAddressSuggestionsByName(
                  getLabelAddress(userSelectedAddressRef.current, { withNumber: true }, company?.company_address[0]?.geo_coordinates)
                )
                if (!!addressSuggestionsByName) {
                  const addressCoordinates = await getAddressCoordinates(addressSuggestionsByName[0].mapbox_id)
                  if (!!addressCoordinates) {
                    const { latitude, longitude } = addressCoordinates
                    setModalMapAddress({
                      status: true,
                      location: { latitude, longitude },
                    })
                    editAddressRef.current = { ...userSelectedAddressRef.current?.customer_address }
                  } else {
                    setModalInitialAddress({ status: true, content: { source: "orderFinisher" } })
                  }
                } else {
                  setModalInitialAddress({ status: true, content: { source: "orderFinisher" } })
                }
              }
            })
          finalizingOrderRef.current = false
          setFinalizingOrder(false)
          setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })

          return false
        }
      }

      //Verificando se o total do pedido confere com o valor em estado para saber se os itens continuam corretos

      const [total, subTotal, benefitTotal] = getTotalOrder(order, userSelectedAddress?.deliveryFee)

      const deliveryFeeValue = modality !== 2 && modality !== 3 ? userSelectedAddress?.deliveryFee || 0 : 0

      const discount = getDiscountOrder(order, subTotal, deliveryFeeValue, settings, modality)

      const subTotalOrder = total + deliveryFeeValue - benefitTotal - discount

      // if (subTotal !== subTotalOrder) {
      //   SupportController.throwNewLog({
      //     type: "warning",
      //     content: { customer: user, subTotal, benefit },
      //     message: "Pedido não enviado, falha ao identificar valor total do pedido",
      //   }).then()
      //   setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
      //
      //   onMessageDialog()
      //     .fire({
      //       title: "Falha ao finalizar o pedido, selecione novamente seus itens e tente reenviar o pedido! 😕",
      //       icon: "warning",
      //       timer: 4000,
      //       showCancelButton: false,
      //       showConfirmButton: false,
      //     })
      //     .then()
      //   finalizingOrderRef.current = false
      //   setFinalizingOrder(false)
      //
      //   return false
      // }

      // Verificando se existe forma de pagamento selecionada e se o total do pagamento é o mesmo do pedido
      let orderInsert = { ...order }

      // ############## VALIDAÇÕES DOS PAGAMENTOS
      // Verificar se existe ao menos 1 pagamento
      if (order.payments && order.payments.length === 0) {
        setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
        SupportController.throwNewLog({
          type: "warning",
          content: { customer: userRef.current },
          message: "Pedido não enviado, Falha na confirmação dos pagamentos",
        }).then()
        onMessageDialog()
          .fire({
            title: "Selecione ao menos uma forma de pagamento e tente reenviar o pedido! 😕",
            icon: "warning",
            timer: 4000,
            showCancelButton: false,
            showConfirmButton: false,
          })
          .then()
        finalizingOrderRef.current = false
        setFinalizingOrder(false)
        setShowPaymentTypeSelector(true)

        return false
      }
      // Verificar se o valor do(s) pagamento(s) é igual ao valor do subtotal do pedido
      let orderPayment = null
      let cashBackPayment = null
      let totalPaymentValidation = 0

      // Separa o pagamento normal do pagamento em cashback e calculando o valor total pago
      for (const payment of order.payments || []) {
        totalPaymentValidation += payment.val_payment

        if (payment.nm_payment_method.toLowerCase() === "cashback") {
          cashBackPayment = payment
        } else {
          orderPayment = payment
        }
      }

      //Caso o valor total a ser pago esteja diferente do valor total do pedido, é possível ajustar o valor da forma de pagamento em algumas situações
      //Para que o pedido não fique travado.
      if (subTotalOrder !== totalPaymentValidation) {
        let requestPayment = false

        if (orderPayment) {
          if (
            // É qualquer método de pagamento
            orderPayment?.id_payment_method !== 1 ||
            //Pagamento é em dinheiro E o valor do pagamento é maior que o valor do pedido
            (orderPayment?.id_payment_method === 1 &&
              (orderPayment?.val_receivable > subTotalOrder ||
                // Pagamento não possui valor a receber (Isto ocorre quando é preenchido qualquer forma de pagamento diferente de dinheiro
                // Pois nesse caso nós não informamos que o valor a receber, apenas o valor pago.)
                orderPayment?.val_receivable === 0))
          ) {
            let payments = [
              {
                ...orderPayment,
                val_payment: subTotalOrder - (cashBackPayment?.val_payment || 0),
              },
            ]
            if (!!cashBackPayment) {
              payments.push(cashBackPayment)
            }
            //Alterando o order
            orderInsert = {
              ...order,
              payments,
            }
          } else {
            requestPayment = true
          }
        } else {
          if (cashBackPayment && formatBoolean(settings?.allow_order_paid_only_cashback || false)) {
            const totalDiference = subTotalOrder - totalPaymentValidation
            if (Number.isNaN(totalDiference)) {
              setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
              SupportController.throwNewLog({
                type: "warning",
                content: { customer: userRef.current, orderPayment },
                message: "Pedido não enviado, falha na confirmação dos pagamentos",
              }).then()
              onMessageDialog()
                .fire({
                  title: "Selecione novamente sua forma de pagamento e tente reenviar o pedido! 😕",
                  icon: "warning",
                  timer: 4000,
                  showCancelButton: false,
                  showConfirmButton: false,
                })
                .then()
              finalizingOrderRef.current = false
              setFinalizingOrder(false)
              setShowPaymentTypeSelector(true)

              return false
            }
            if (totalDiference < 0.01) {
              cashBackPayment.val_payment += totalDiference
              let payments = [cashBackPayment]
              orderInsert = {
                ...order,
                payments,
              }
            } else {
              order.payments[0].val_payment += totalDiference
            }
          } else {
            requestPayment = true
          }
        }
        if (requestPayment) {
          setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
          SupportController.throwNewLog({
            type: "warning",
            content: { customer: userRef.current, orderPayment },
            message: "Pedido não enviado, Falha na confirmação dos pagamentos",
          }).then()
          onMessageDialog()
            .fire({
              title: "Selecione novamente sua forma de pagamento e tente reenviar o pedido! 😕",
              icon: "warning",
              timer: 4000,
              showCancelButton: false,
              showConfirmButton: false,
            })
            .then()
          finalizingOrderRef.current = false
          setFinalizingOrder(false)

          setOrder({ ...order, payments: order.payments.filter(({ nm_payment_method }) => nm_payment_method === "Cashback") })
          setSelectedPaymentMethod({
            id_payment_method: -1,
          })
          setShowPaymentTypeSelector(true)

          return false
        }
      }

      // Verificar se existe apenas pagamentos de cashback e se a configuração de permitir pedidos pagos apenas com cashback está valida (allow_order_paid_only_cashback)
      const cashbackPaymentValue = cashBackPayment ? cashBackPayment?.val_payment || 0 : 0
      const orderTotal = subTotalOrder - cashbackPaymentValue
      orderInsert = {
        ...orderInsert,
        discountType:
          modality === 2 ? settings.pickup_discount_type : modality === 3 ? settings.indoor_discount_type : settings.delivery_discount_type,
        discount:
          modality === 2
            ? parseFloat(settings.pickup_discount_value || 0)
            : modality === 3
            ? parseFloat(settings.indoor_discount_value || 0)
            : parseFloat(settings.delivery_discount_value || 0),
        orderTotal,
      }

      if (![3, 2].includes(modality)) {
        let addresses = [...userRef.current.addresses]

        if (!!editAddressRef.current) {
          addresses = userRef.current.addresses.filter(
            ({ customer_address }) => customer_address?.id_customer_address !== editAddressRef.current?.customer_address?.id_customer_address
          )
          editAddressRef.current = null
        } else {
          if (!!userSelectedAddressRef.current.customer_address.id_customer_address) {
            addresses = userRef.current.addresses.filter(
              ({ customer_address }) => customer_address?.id_customer_address !== userSelectedAddressRef.current.customer_address.id_customer_address
            )
          }
        }

        const originalListAddresses = addresses.map((address) => {
          return { ...address, st_primary: false }
        })

        if (!!userSelectedAddressRef.current.customer_address.dt_deleted_at) {
          userSelectedAddressRef.current = {
            ...userSelectedAddressRef.current,
            customer_address: { ...(userSelectedAddressRef?.current?.customer_address || {}), dt_deleted_at: null, id_customer_address: null },
            st_primary: true,
          }
        }
        CustomerController.update(userRef.current.uuid_customer, {
          addresses: [...originalListAddresses].concat([
            {
              ...userSelectedAddressRef.current,
              st_primary: true,
            },
          ]),
        })
          .then(async (responseAddressUpdate) => {
            if (responseAddressUpdate.status === 200) {
              const addresses = responseAddressUpdate.data?.streets || []
              const defaultAddress = addresses.find(
                ({ customer_address: { st_primary, dt_deleted_at }, nm_street }) =>
                  st_primary &&
                  !dt_deleted_at &&
                  escapeSpecialChart(nm_street).toLowerCase() === escapeSpecialChart(userSelectedAddressRef?.current?.nm_street).toLowerCase()
              )

              if (!!defaultAddress && defaultAddress.id_street && defaultAddress?.neighbourhood?.id_neighbourhood) {
                let updatedUser = { ...userRef.current, addresses }
                setUser(updatedUser)
                userRef.current = updatedUser
                const addressUpdated = await onChangeUserSelectedAddress(defaultAddress, true)
                if (!addressUpdated) {
                  SupportController.throwNewLog({
                    type: "warning",
                    content: { customer: userRef.current, addressUpdated, defaultAddress },
                    message: "Pedido não enviado, Erro na atualização do cliente",
                  }).then()
                  setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })

                  onMessageDialog()
                    .fire({
                      title: "Falha ao verificar o processamento do seu endereço, tente novamente!",
                      icon: "warning",
                      timer: 4000,
                      showCancelButton: false,
                      showConfirmButton: false,
                    })
                    .then()
                  finalizingOrderRef.current = false
                  setFinalizingOrder(false)

                  return false
                }

                setLocalItem("user", updatedUser, true)
                const customer = {
                  defaultAddress,
                  userSelectedAddress: {
                    ...addressUpdated,
                  },
                  uuid_customer: userRef.current.uuid_customer,
                  nm_customer: userRef.current.nm_customer,
                  id_street_customer: defaultAddress.id_street,
                  id_customer_address: defaultAddress?.customer_address?.id_customer_address,
                  id_neighbourhood: defaultAddress?.neighbourhood?.id_neighbourhood,
                }

                orderInsert = {
                  ...orderInsert,
                  id_modality: modality,
                  deliveryFee: !!addressUpdated.deliveryFee ? formatFloat(addressUpdated.deliveryFee) : 0,
                  deliveryTime: !!addressUpdated.deliveryTime ? addressUpdated.deliveryTime : Number(settings?.delivery_time) || 0,
                  st_resended,
                }
                insertOrder(orderInsert, customer, orderType, scheduleInfo, onlinePayment, typeOnlinePayment, newRequest)
              } else {
                SupportController.throwNewLog({
                  type: "warning",
                  content: { customer: userRef.current, defaultAddress },
                  message: "Pedido não enviado, Falha ao identificar o endereço",
                }).then()
                setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })

                onMessageDialog()
                  .fire({
                    title: "Não conseguimos identificar o seu endereço, por favor selecione novamente! 😕",
                    icon: "warning",
                    timer: 4000,
                    showCancelButton: false,
                    showConfirmButton: false,
                  })
                  .then()
                finalizingOrderRef.current = false
                setFinalizingOrder(false)
              }
            } else {
              setUser({ ...getLocalItem("user", true) })
              userRef.current = { ...getLocalItem("user", true) }
              SupportController.throwNewLog({
                type: "error",
                content: { customer: userRef.current, responseAddressUpdate },
                message: "Pedido não enviado, falha ao atualizar o endereço",
              }).then()
              setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })

              onMessageDialog("danger")
                .fire({
                  title: "Não conseguimos atualizar o seu endereço, por favor tente novamente! (1)",
                  icon: "error",
                  timer: 3000,
                  showCancelButton: false,
                  showConfirmButton: true,
                  confirmButtonText: "Confirmar",
                  reverseButtons: true,
                })
                .then()
              finalizingOrderRef.current = false
              setFinalizingOrder(false)
            }
          })
          .catch((error) => {
            console.error("error", error)

            SupportController.throwNewLog({
              type: "error",
              content: { customer: userRef.current, errorMessage: error.message },
              message: "Pedido não enviado, catch da atualização do cliente",
            }).then()
            setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })

            onMessageDialog("danger")
              .fire({
                title: "Não conseguimos atualizar os dados para realizar o pedido, tente novamente!",
                icon: "error",
                timer: 3000,
                showCancelButton: false,
                showConfirmButton: false,
              })
              .then()
            finalizingOrderRef.current = false

            setFinalizingOrder(false)
          })
      } else if ([3, 2].includes(modality)) {
        const customer = {
          uuid_customer: userRef.current.uuid_customer,
          nm_customer: userRef.current.nm_customer,
          id_street_customer: null,
          id_customer_address: null,
          id_neighbourhood: null,
        }
        orderInsert = {
          ...orderInsert,
          id_modality: modality,
          deliveryFee: 0,
          deliveryTime: modality === 2 ? Number(settings.pickup_time) : Number(settings.indoor_time),
          st_resended,
        }

        insertOrder(orderInsert, customer, orderType, scheduleInfo, onlinePayment, typeOnlinePayment, newRequest)
      }
    }
  }

  const insertOrder = async (orderInsert, customer, orderType, scheduleInfo, onlinePayment, typeOnlinePayment, newRequest, retry = false) => {
    if (onlinePayment) {
      switch (typeOnlinePayment) {
        case "pix":
          const isPixelInitialized = !!activePixelId.pixelId
          console.log("isPixelInitialized", isPixelInitialized)
          const parsedOnlinePaymentOrder = getParamsToCreateOrder(orderInsert, customer, isPixelInitialized)
          PaymentsController.createPayment(parsedOnlinePaymentOrder, null, customer, "pix", newRequest).then((response) => {
            if (response.status === 422) {
              setModalOrderProcess({ status: true, resend: true, newRequest: true, scheduleInfo, content: { onlinePayment, typeOnlinePayment } })
              finalizingOrderRef.current = false
              setFinalizingOrder(false)
            } else if (response.status === 200) {
              setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
              setModalPixPayment({ status: true, content: response.data })
              modalPixPaymentContent.current = response.data
              clearOrder()
            } else {
              setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
              finalizingOrderRef.current = false
              setFinalizingOrder(false)
              onMessageDialog("danger")
                .fire({
                  title: "Falha ao gerar o pagamento online, tente novamente!",
                  icon: "error",
                  timer: 3000,
                  showCancelButton: false,
                  showConfirmButton: false,
                  reverseButtons: true,
                })
                .then()
            }
          })
        default:
          break
      }
    } else {
      const content = { orderInsert, customer, orderType, scheduleInfo, onlinePayment, typeOnlinePayment, newRequest }

      OrderController.insert(orderInsert, customer, orderType, scheduleInfo)
        .then((response) => {
          if (response.status === 201) {
            const newOrder = response.data

            setModalOrderProcess({
              status: false,
              resend: false,
              newRequest: false,
              scheduleInfo: null,
              content: { onlinePayment, typeOnlinePayment },
            })

            //Atualizando usuário da sessão setUser(updatedUser)
            const updatedUser = {
              ...userRef.current,
              customer_orders: [
                {
                  fk_id_customer_address: response?.data?.customer_address_order?.id_customer_address,
                  id_order: response?.data?.id_order,
                  id_street_customer: response?.data?.id_street_customer,
                  st_confirmed: false,
                  dt_deleted_at: null,
                },
              ],
            }
            setUser(updatedUser)
            userRef.current = updatedUser
            setLocalItem("user", updatedUser, true)

            if (orderType === "schedule") {
              // Sinalizando o registro de um novo pedido agendado
              console.info("EMITINDO EVENTO [Home] [schedule_order]")
              !!socket &&
                socket.emit("schedule_order", {
                  origin: "site",
                  companyId: company.id_company,
                })

              console.info("EMITINDO EVENTO [Home] [signal_schedule_order]")
              !!socket &&
                socket.emit("signal_schedule_order", {
                  origin: "site",
                  companyId: company.id_company,
                })

              ReactPixel.track("Purchase", { value: newOrder.val_subtotal, currency: "BRL" })

              onMessageDialog("sucess")
                .fire({
                  title: "Pedido agendado com sucesso!",
                  icon: "success",
                  timer: 3000,
                  showCancelButton: false,
                  showConfirmButton: false,
                })
                .then(({ isDismissed }) => {
                  if (isDismissed) {
                    // goToOrderProgressRef.current = { status: false, scheduleOrderUid: newOrder.uuid_schedule_order }
                    showOrderProgressContextRef.current = { status: false, scheduleOrderUid: newOrder.uuid_schedule_order }

                    setTabSelected("profile")
                  }
                })
            } else {
              ReactPixel.track("Purchase", { value: newOrder.val_subtotal, currency: "BRL" })

              onMessageDialog("sucess")
                .fire({
                  title: "Pedido realizado com sucesso!",
                  icon: "success",
                  timer: 3000,
                  showCancelButton: false,
                  showConfirmButton: false,
                })
                .then(({ isDismissed }) => {
                  if (isDismissed) {
                    // goToOrderProgressRef.current = { status: true, orderUid: newOrder.uuid_order, order: newOrder }
                    showOrderProgressContextRef.current = { status: true, orderUid: newOrder.uuid_order, content: newOrder }

                    setTabSelected("profile")
                  }
                })
            }

            if (order.id_fidelity) {
              const fidelityUsed = fidelities.find(({ id_fidelity }) => order.id_fidelity === id_fidelity)
              if (fidelityUsed) {
                const pointsNeeded = fidelityUsed.minimum_value || 0
                let fidelityUsageControl = getLocalItem("fidelityUsageControl", true) || {}

                let fidelityGroupItemName = null
                // Verifica se o order.id_fidelity faz parte de um grupo
                for (const [key, value] of Object.entries(groupFidelitiesRef.current)) {
                  if (value.includes(order.id_fidelity)) {
                    fidelityGroupItemName = key
                    break
                  }
                }

                // const fidelityGroupInfo = groupFidelitiesRef.current.find((g) => g.id_fidelity === order.id_fidelity)
                const fidelitiesToDeduct =
                  fidelityGroupItemName !== null
                    ? groupFidelitiesRef.current[fidelityGroupItemName]
                    : // ? groupFidelitiesRef.current.filter((g) => g.id_group_fidelity === fidelityGroupInfo.id_group_fidelity).map((g) => g.id_fidelity)
                      [order.id_fidelity]

                fidelitiesToDeduct.forEach((idFidelity) => {
                  const fidelityInfo = fidelities.find((f) => f.id_fidelity === idFidelity)
                  if (!fidelityInfo) return

                  const userFidelityPoints = user.fidelities.filter((point) => point.fk_id_fidelity === idFidelity && !point.st_used)

                  const pointsToUse = userFidelityPoints.slice(0, pointsNeeded)
                  if (!fidelityUsageControl[idFidelity]) {
                    fidelityUsageControl[idFidelity] = []
                  }

                  fidelityUsageControl[idFidelity].push({
                    pointsUsed: pointsToUse.map((p) => p.id_fidelity_customer),
                    usedAt: Date.now(),
                  })
                })

                setLocalItem("fidelityUsageControl", fidelityUsageControl, true)
              } else {
                SupportController.throwNewLog({
                  type: "error",
                  content: {
                    customer: userRef.current,
                    order,
                    errorMessage: `Foi usada uma fidelidade no site, porém ocorreu um erro na rotina. Fidelidade usada:${order.id_fidelity}`,
                  },
                  message: "Pedido não enviado, catch da inserção do pedido",
                }).then()
              }
            }

            if (order.benefits.length > 0) {
              let localBenefitsUsed = getSessionItem("benefitsUsed", true)

              if (!!localBenefitsUsed) {
                localBenefitsUsed.push(order.id_benefit)
              } else {
                localBenefitsUsed = [order.id_benefit]
              }

              setSessionItem("benefitsUsed", localBenefitsUsed, true)
            }

            clearOrder()
          } else if (response.status === 200) {
            if (!retry) {
              onMessageDialog("danger")
                .fire({
                  title: response?.message || "Falha ao verificar se o pedido foi processado, tente novamente!",
                  icon: "error",
                  timer: 3000,
                  showCancelButton: false,
                  showConfirmButton: false,
                  reverseButtons: true,
                })
                .then()
            }
            finalizingOrderRef.current = false
            setFinalizingOrder(false)
            setModalOrderProcess({ status: true, resend: true, newRequest: false, scheduleInfo, content })
          } else if (response.status === 400) {
            if (!retry) {
              onMessageDialog("danger")
                .fire({
                  title: response?.data?.message || "Erro ao processar o pedido, tente novamente!",
                  icon: "error",
                  timer: 3000,
                  showCancelButton: false,
                  showConfirmButton: false,
                  reverseButtons: true,
                })
                .then()
            }
            finalizingOrderRef.current = false
            setFinalizingOrder(false)
            setModalOrderProcess({ status: true, resend: true, newRequest: false, scheduleInfo, content })
          } else {
            // if (!retry) {
            //   onMessageDialog("danger")
            //     .fire({
            //       title: response?.data?.message || response?.message || "Ocorreu um erro ao processar o pedido, tente novamente!",
            //       icon: "error",
            //       timer: 3000,
            //       showCancelButton: false,
            //       showConfirmButton: false,
            //       reverseButtons: true,
            //     })
            //     .then()
            // }
            finalizingOrderRef.current = false
            setFinalizingOrder(false)
            setModalOrderProcess({ status: true, resend: true, newRequest: false, scheduleInfo, content })
          }
        })
        .catch((error) => {
          console.error("error", error)
          SupportController.throwNewLog({
            type: "error",
            content: { customer: userRef.current, order, errorMessage: error.message },
            message: "Pedido não enviado, catch da inserção do pedido",
          }).then()
          if (!retry) {
            onMessageDialog("danger")
              .fire({
                title: "Houve uma falha ao processar o pedido, tente novamente!",
                icon: "error",
                timer: 3000,
                showCancelButton: false,
                showConfirmButton: true,
                confirmButtonText: "Confirmar",
                reverseButtons: true,
              })
              .then()
          }
          finalizingOrderRef.current = false
          setFinalizingOrder(false)

          setModalOrderProcess({ status: true, resend: true, newRequest: false, scheduleInfo, content })
        })
    }
  }

  const handleOutsideCoverageArea = (value) => {
    setIsOutsideCoverageArea(value)
  }

  const onAddBeverages = (beverages, selectedItem = null) => {
    const colors = [
      { id: 1, used: false, color: "#F2D7D5" },
      { id: 2, used: false, color: "#D4E6F1" },
      { id: 3, used: false, color: "#D4EFDF" },
      { id: 4, used: false, color: "#FDEBD0" },
      { id: 5, used: false, color: "#FADBD8" },
      { id: 6, used: false, color: "#EBDEF0" },
      { id: 7, used: false, color: "#D6EAF8" },
      { id: 8, used: false, color: "#D1F2EB" },
      { id: 9, used: false, color: "#D6DBDF" },
      { id: 10, used: false, color: "#F4ECF7" },
      { id: 11, used: false, color: "#FAE5D3" },
    ]

    if (!!selectedItem) {
      //verificando quantidade de produto com mesmo id
      const amountProduct =
        !!order.products &&
        order.products.reduce((acumulator, product) => {
          return product.uuid_product === selectedItem.products.uuid_product ? acumulator + 1 : acumulator
        }, 0)

      order.products.push({
        ...selectedItem.products,
        nro_product: amountProduct + 1,
        backgroundColor: colors[amountProduct].color,
      })

      for (const beverage of selectedItem.beverages) {
        order.beverages.push({
          ...beverage,
          amount: beverage.amount || 1,
          nro_product: amountProduct + 1,
          backgroundColor: colors[amountProduct].color,
        })
      }
    } else {
      for (const beverage of beverages) {
        const indexBeverage = order.beverages.findIndex(
          ({ id_beverage, id_product, nro_item }) => !nro_item && !id_product && id_beverage === beverage.id_beverage
        )

        if (indexBeverage === -1) {
          order.beverages.push({ ...beverage })
        }
      }
    }

    ReactPixel.track("AddToCart", {})
    onChangeOrder({ ...order })
    setModalBuilder({ ...modalBuilder, status: false, content: null, size: null })
    setModalBeverages({ status: false, content: null })
  }

  //CONTENT FUNCTIONS
  const onClickItem = (type, content) => {
    switch (type) {
      case "category":
        setModalBeverages({ status: true, category: content })
        break
      case "beverage":
        // Verificando se já existe a bebida na lista de pedido
        if (
          !order.beverages.find(
            ({ id_beverage, id_product, nro_product = null, nro_combo = null, nm_beverage }) =>
              !id_product && id_beverage === content.id_beverage && nro_product === null && nro_combo === null
          )
        ) {
          const newOrder = { ...order, beverages: [...order.beverages, { ...content, amount: 1 }] }

          const [total] = getTotalOrder(newOrder)
          setOrder({ ...newOrder, total, subTotal: total })

          ReactPixel.track("AddToCart", { nm_item: content?.nm_beverage })

          GeneralTools.swalMessage("success")
            .fire({
              title: `${getBeverageName(content)} inserida a sua sacola!`,
              icon: "success",
              timer: 2000,
              showCancelButton: false,
              showConfirmButton: false,
            })
            .then()
        } else {
          GeneralTools.swalMessage("success")
            .fire({
              title: `${getBeverageName(content)} já está em sua sacola!`,
              icon: "info",
              timer: 2000,
              showCancelButton: false,
              showConfirmButton: false,
            })
            .then()
        }
        break
      case "flavour":
        // const { uuid_product, flavour, productIndex } = content

        //Filtrando os tamanhos que não estão habilitados para o site
        const flavourSizes = content.flavour.sizes.filter(({ st_website }) => st_website).sort((a, b) => a.nro_priority - b.nro_priority)
        setModalBuilder({
          ...modalBuilder,
          type,
          status: true,
          content: {
            ...content,
            size: flavourSizes[0],
          },
        })

        break
      case "highlight":
        const highlightBeverage = content.highlight_beverages.length > 0
        const highlightCombo = content.highlight_combos.length > 0
        const highlightProduct = content.highlight_products.length > 0

        if (highlightBeverage) {
          const beverageSelected = beverages.find(({ id_beverage }) => id_beverage === content.highlight_beverages[0].id_beverage)
          onClickItem("beverage", beverageSelected)
        } else if (highlightCombo) {
          const comboSelected = combos.find(({ id_combo }) => id_combo === content.highlight_combos[0].id_combo)
          setModalBuilder({ status: true, type: "combo", content: { combo: comboSelected } })
        } else if (highlightProduct) {
          const product = content.highlight_products[0].highlights_products
          let productSelected = products.find(({ id_product }) => id_product === product.fk_id_product)

          if (productSelected.st_display_mode === "size") {
            productSelected = {
              ...productSelected,
              size: productSelected.sizes.find(({ id_size }) => id_size === product.fk_id_size),
            }
            delete productSelected.sizes
            setModalBuilder({ status: true, type: "size", content: productSelected })
          } else {
            const highlightFlavour = productSelected.flavours.find(({ id_flavour }) => id_flavour === product.fk_id_flavour)

            setModalBuilder({
              type: "flavour",
              status: true,
              content: {
                size: productSelected.sizes[0],
                flavour: highlightFlavour,
                uuid_product: productSelected.uuid_product,
              },
            })
          }
        }

        break
      default:
        setModalBuilder({ status: true, type, content })
        break
    }
  }

  const onShowModalSchedule = () => {
    if ([3, 2].includes(modality) || !!userSelectedAddress) {
      setModalOrderSchedule(true)
    } else {
      onMessageDialog("info")
        .fire({
          title: "Para agendar um pedido é preciso escolher um endereço",
          icon: "info",
          timer: 3000,
          showCancelButton: false,
          showConfirmButton: false,
        })
        .then()
    }
  }

  const onChangeAmountProduct = (amount, indexProduct) => {
    let products = order.products
    if (amount > 0) {
      let newProducts = products.map((product, index) => {
        if (index === indexProduct) {
          product.amount = amount
          return product
        }
        return product
      })

      let newBeverages = order.beverages.map((beverage, index) => {
        if (index === indexProduct) {
          beverage.amount = amount
          return beverage
        }
        return beverage
      })

      const newOrder = {
        ...order,
        products: newProducts,
        beverage: newBeverages,
      }

      const [total] = newOrder
      setOrder({ ...newOrder, total, subTotal: total })
    } else if (amount === 0) {
      GeneralTools.swalMessage("delete")
        .fire({
          title: "Deseja remover o item do carrinho?",
          icon: "question",
          cancelButtonText: "Cancelar",
          showCancelButton: true,
          confirmButtonText: "Confirmar",
          showConfirmButton: true,
          reverseButtons: true,
        })
        .then((response) => {
          if (response.isConfirmed) {
            let newProducts = products
              .filter((product, index) => {
                if (index === indexProduct) {
                  return null
                }
                return product
              })
              .filter((eachElement) => Object.keys(eachElement).length > 0)

            setOrder({
              ...order,
              products: newProducts,
            })
          }
        })
    }
  }

  const onChangeOrderBuilder = (item, selectedItem, comboRemoved = null) => {
    if (!!item) {
      if ("id_combo" in item) {
        order.combos.push({ ...item, amount: selectedItem.amount, backgroundColor: getBackgroundColor(currentOrderCombo) })

        if (!!selectedItem.products) {
          for (const product of selectedItem.products) {
            order.products.push({
              ...product,
              amount: 1,
              id_combo: product.id_combo,
              nro_item: product.nro_item,
              nro_combo: product.nro_combo,
            })

            for (const beverage of product.beverages) {
              order.beverages.push({
                ...beverage,
                id_combo: item.id_combo,
                nro_combo: item.nro_combo,
                nro_item: product.nro_item,
              })
            }
          }
        }

        if (!!selectedItem.beverages) {
          for (const beverage of selectedItem.beverages) {
            order.beverages.push({
              ...beverage,
              id_combo: item.id_combo,
              nro_combo: item.nro_combo,
              nro_item: beverage.nro_item,
            })
          }
        }

        // Removendo os dados do combo antigo
        if (!!comboRemoved) {
          const comboIndex = order.combos.findIndex(
            ({ nro_combo, id_combo }) => nro_combo === comboRemoved?.comboNro && id_combo === comboRemoved?.comboId
          )
          if (comboIndex !== -1) {
            order.combos.splice(comboIndex, 1)
            order.products = order.products.filter(
              ({ nro_combo, id_combo }) => nro_combo !== comboRemoved?.comboNro || id_combo !== comboRemoved?.comboId
            )
            order.beverages = order.beverages.filter(
              ({ nro_combo, id_combo }) => nro_combo !== comboRemoved?.comboNro || id_combo !== comboRemoved?.comboId
            )
          }
        }
      } else {
        if (selectedItem.size.beverages.length > 0) {
          setModalBeverages({
            status: true,
            content: null,
            data: {
              isMandatory: selectedItem.size.st_required_beverage,
              maxBeverages: selectedItem.size.qty_beverages,
              selectedItem,
            },
          })
        } else {
          const amountProduct =
            !!order.products &&
            order.products.reduce((acumulator, product) => {
              return product.fk_id_product === selectedItem.fk_id_product ? acumulator + 1 : acumulator
            }, 0)

          order.products.push({ ...selectedItem, nro_product: amountProduct + 1 })
        }
      }

      const [total, subTotal] = getTotalOrder({ ...order })
      setSessionItem("order", { ...order, total, subTotal }, true)
      setOrder({ ...order, total, subTotal })
    } else {
      Message.info("Erro ao identificar o item escolhido")
    }
  }

  // Função para obter a cor de fundo de cada item do pedido
  const getBackgroundColor = (id = null) => {
    // Verificando se uma cor possui um identificador igual
    const indexID = colors.findIndex((item) => item.used === true && item.id === id)
    if (indexID !== -1) {
      return colors[indexID].color
    }

    // Buscando uma cor não utilizada
    const indexColor = colors.findIndex((item) => item.used === false && !!id)
    if (indexColor !== -1) {
      // Atualizando a lista de cores
      colors[indexColor] = { ...colors[indexColor], used: true }
      setColors([...colors])
      return colors[indexColor].color
    }

    // Atualizando a lista de cores
    colors[colors.length - 1] = { ...colors[colors.length - 1], used: true }
    return colors[colors.length - 1].color
  }

  const onUpdate = (company) => {
    setUserSelectedAddress(null)
    removeLocalItem("userSelectedAddress")
    setModalCompanies({ status: false, content: null })
    getContent(company, false, true).then()
  }

  const onSelectedCompany = async (company, showCancelButton = true, text = `A loja está fechada! Deseja prosseguir?`) => {
    if (!company.st_website) {
      let fowardToClosedCompany = false
      await GeneralTools.swalMessage("delete")
        .fire({
          title: text,
          icon: "info",
          showCancelButton: showCancelButton,
          cancelButtonText: "Voltar",
          showConfirmButton: true,
          confirmButtonText: "Prosseguir",
          reverseButtons: true,
        })
        .then(({ isConfirmed }) => {
          fowardToClosedCompany = isConfirmed
        })
      if (!fowardToClosedCompany) return null
    }
    if (!company.url_redirect) {
      // Vai definir que o pedido está fora da área de entrega
      // handleOutsideCoverageArea(isOutsideCoverageArea)
      setLocalItem("company", company)
      // Limpando todos os dados salvos no Storage
      removeLocalItem("menuOptions")
      removeLocalItem("settings")

      removeLocalItem("highlights")
      removeLocalItem("combos")

      removeLocalItem("products")
      removeLocalItem("productSizes")
      removeLocalItem("productFlavours")

      removeLocalItem("beverages")
      removeLocalItem("schedules")

      removeLocalItem("deliveryFee")
      removeLocalItem("deliveryTime")

      onUpdate(company)
    } else {
      GeneralTools.swalMessage("delete")
        .fire({
          title: `Você será direcionado ao endereço desta loja, deseja prosseguir?`,
          icon: "info",
          showCancelButton: true,
          cancelButtonText: "Voltar",
          showConfirmButton: true,
          confirmButtonText: "Prosseguir",
          reverseButtons: true,
        })
        .then(({ isConfirmed }) => {
          if (isConfirmed) {
            setLoading(true)
            window.open(company.url_redirect, "_self")
          }
        })
    }
  }

  const setLocalCompany = async (storageCompany, companyIds, whatsappCompanyId = null) => {
    let newCompany = storageCompany
    let setCompanyLocal = false
    let startLoad = false
    let refreshOrder = true
    let companies = getLocalItem("companies", true) || []

    if (companies.length > 0 && !!newCompany && !whatsappCompanyId) {
      setCompanyLocal = true
      refreshOrder = false
      startLoad = true
    } else if (companies.length > 0 && !!newCompany && !!whatsappCompanyId && newCompany.id_company === whatsappCompanyId) {
      setCompanyLocal = true
      refreshOrder = true
      startLoad = false
    } else {
      const responseCompanies = await CompanyController.fetchAll(true, companyIds)

      if (responseCompanies.status === 200) {
        companies = responseCompanies?.data || []

        if (!!whatsappCompanyId) {
          newCompany = companies.find((company) => company.id_company === whatsappCompanyId)
        }

        if (!newCompany) {
          newCompany = companies[0]
        }

        if (!storageCompany && !whatsappCompanyId && companies.length > 1) {
          setModalCompanies({ status: true, content: companies })
        } else if (companies.length === 1) {
          onSelectedCompany(companies[0], false, "A Loja está fechada neste momento!")
        }

        setCompanyLocal = !!newCompany

        startLoad = true
      }
    }

    if (setCompanyLocal) {
      companyRef.current = newCompany
      setCompanies(companies)
      setLocalItem("companies", companies, true)
      // ----------------------------------------------------------------------
      getContent(newCompany, startLoad, refreshOrder, companies).then() // Obtendo conteúdo do site
      // ----------------------------------------------------------------------
    }
  }

  const clearOrderProgressContext = () => {
    showOrderProgressContextRef.current = { status: false, orderUid: null, scheduleOrderUid: null }
  }

  let orderItems = []
  if (!!settings.site_show_order) {
    orderItems = JSON.parse(settings.site_show_order)
  }

  let siteContent = []
  if (orderItems.length > 0) {
    orderItems.forEach((eachItem) => {
      switch (eachItem.name) {
        case "destaques":
          siteContent.push(<Highlights onClickHighlight={onClickItem} key={eachItem.id} />)
          break
        case "combos":
          siteContent.push(<Combos onClickCombo={onClickItem} key={eachItem.id} />)
          break
        case "produtos":
          siteContent.push(<Products onClickProduct={onClickItem} key={eachItem.id} />)
          break
        case "bebidas":
          siteContent.push(<Beverages onClickBeverage={onClickItem} onClickCategory={onClickItem} key={eachItem.id} />)
          break
      }
    })
  } else {
    siteContent = [
      <Highlights onClickHighlight={onClickItem} key={"_highlight"} />,
      <Combos onClickCombo={onClickItem} key={"_combos"} />,
      <Products onClickProduct={onClickItem} key={"_products"} />,
      <Beverages onClickBeverage={onClickItem} onClickCategory={onClickItem} key={"_beverages"} />,
    ]
  }

  const ErrorFallback = ({ error, resetErrorBoundary }) => {
    return (
      <div className="error-boundary">
        <div className="error-boundary--body">
          <img alt="" src={logoImage} />
          <h6>Erro ao carregar os dados do cardápio virtual.</h6>
          <h6>Favor entrar em contato com o estabelecimento urgente!</h6>
          <div>
            <Label>Descrição do erro</Label>
            <pre>{error.message}</pre>
            <Button onClick={resetErrorBoundary}>Recarregar</Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error) => {
        console.error("ERRO AO CARREGAR O SITE => ", error)
      }}
    >
      <Suspense
        fallback={
          <div style={{ width: "100%" }} className="app--loading">
            <div className="app--loading--top">
              <Skeleton style={{ width: "100%" }} height={175} />
              <div className="app--loading--top--circle">
                <Skeleton className="circle" height={100} width={100} circle />
              </div>

              <div className="app--loading--top--tax">
                <Skeleton style={{ width: "100%" }} height={50} />
              </div>
            </div>
            <div className="app--loading--center">
              <Skeleton baseColor="#c8c8c8" height={24} />
              <Skeleton baseColor="#c8c8c8" height={24} />
              <Skeleton baseColor="#c8c8c8" height={24} />
              <Skeleton baseColor="#c8c8c8" height={24} />
              <Skeleton baseColor="#c8c8c8" height={24} />
              <Skeleton baseColor="#c8c8c8" height={24} />
              <Skeleton baseColor="#c8c8c8" height={24} />
              <Skeleton baseColor="#c8c8c8" height={24} />
            </div>
            <div className="app--loading--bottom">
              <div className="app--loading--bottom--module">
                <div className="app--loading--bottom--module--title">
                  <Skeleton width={92} height={24} />
                  <Skeleton width={145} height={14} />
                </div>
                <div className="app--loading--bottom--module--products">
                  <div className="app--loading--bottom--module--products--product">
                    <div className="app--loading--bottom--module--products--product--left">
                      <Skeleton width={143} height={24} />
                      <Skeleton width={189} height={14} />
                      <Skeleton width={143} height={24} />
                    </div>

                    <Skeleton width={120} height={120} />
                  </div>
                  <div className="app--loading--bottom--module--products--product">
                    <div className="app--loading--bottom--module--products--product--left">
                      <Skeleton width={143} height={24} />
                      <Skeleton width={189} height={14} />
                      <Skeleton width={143} height={24} />
                    </div>

                    <Skeleton width={120} height={120} />
                  </div>
                  <div className="app--loading--bottom--module--products--product">
                    <div className="app--loading--bottom--module--products--product--left">
                      <Skeleton width={143} height={24} />
                      <Skeleton width={189} height={14} />
                      <Skeleton width={143} height={24} />
                    </div>

                    <Skeleton width={120} height={120} />
                  </div>
                  <div className="app--loading--bottom--module--products--product">
                    <div className="app--loading--bottom--module--products--product--left">
                      <Skeleton width={143} height={24} />
                      <Skeleton width={189} height={14} />
                      <Skeleton width={143} height={24} />
                    </div>

                    <Skeleton width={120} height={120} />
                  </div>
                  <div className="app--loading--bottom--module--products--product">
                    <div className="app--loading--bottom--module--products--product--left">
                      <Skeleton width={143} height={24} />
                      <Skeleton width={189} height={14} />
                      <Skeleton width={143} height={24} />
                    </div>

                    <Skeleton width={120} height={120} />
                  </div>
                  <div className="app--loading--bottom--module--products--product">
                    <div className="app--loading--bottom--module--products--product--left">
                      <Skeleton width={143} height={24} />
                      <Skeleton width={189} height={14} />
                      <Skeleton width={143} height={24} />
                    </div>

                    <Skeleton width={120} height={120} />
                  </div>
                  <div className="app--loading--bottom--module--products--product">
                    <div className="app--loading--bottom--module--products--product--left">
                      <Skeleton width={143} height={24} />
                      <Skeleton width={189} height={14} />
                      <Skeleton width={143} height={24} />
                    </div>

                    <Skeleton width={120} height={120} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
      >
        <SocketContext.Provider value={{ socket }}>
          <UserContext.Provider value={{ user, setUser, userSelectedAddress, setUserSelectedAddress, setEditAddressRef }}>
            <MenuContext.Provider
              value={{
                menuFixed,
                addMenuOption,
                updateMenu,
                setModalSignIn,
                setModalCompanies,
                modalPaymentMethods,
                modalBeverages,
                setModalBeverages,
                modalBuilder,
                setModalBuilder,
                modalInitialAddress,
                setModalInitialAddress,
                modalOrderPreview,
                setModalOrderPreview,
                setModalMapAddress,
                setModalPaymentMethods,
                setModalCompanyContacts,
                showPaymentTypeSelector,
                setShowPaymentTypeSelector,

                menuOptions: menuOptionsRef.current,
              }}
            >
              <CompanyContext.Provider value={{ company, companies }}>
                <SettingsContext.Provider
                  value={{
                    version,
                    settings,
                    combos,
                    products,
                    highlights,
                    popup,
                    beverages,
                    schedules,
                    fidelities,
                    setFidelities,
                    neighbourhoods,
                    isOutsideCoverageArea,
                    setIsOutsideCoverageArea,
                    currentStep,
                    setCurrentStep,
                    modality,
                    setModality,
                    onChangeUserSelectedAddress,
                  }}
                >
                  <OrderContext.Provider
                    value={{
                      order,
                      currentOrderCombo,
                      deliveryType,
                      finalizingOrder,
                      setDeliveryType,
                      setCurrentOrderCombo,
                      selectedPaymentMethod,
                      setSelectedPaymentMethod,
                      finishOrder,
                      preFinishOrder,
                      clearOrder,
                      setOrder: onChangeOrder,
                      scheduleInfo: scheduleInfoRef.current,
                    }}
                  >
                    <TabContext.Provider
                      value={{
                        tabSelected,
                        setTabSelected: (tab) => {
                          setSessionItem("order", order, true)
                          setTabSelected(tab)
                        },
                        hideTabs,
                        setHideTabs,
                        goToOrders: goToOrdersRef.current,
                        moveToOrders: (status) => {
                          goToOrdersRef.current = status
                        },
                        goToOrderProgress: goToOrderProgressRef.current,
                        setGoToOrderProgress: (value) => {
                          goToOrderProgressRef.current = value
                        },
                        backToOrderProgress: () => {
                          goToOrderProgressRef.current = { status: false, orderUid: null, scheduleOrderUid: null }
                        },
                        showOrderProgressContext: showOrderProgressContextRef.current,
                        goToFidelity: goToFidelityRef.current,
                        moveToFidelity: () => {
                          goToFidelityRef.current = true
                        },
                        setModalPixPayment: (value) => {
                          setModalPixPayment(value)
                          modalPixPaymentContent.current = value.content
                        },
                        goToOrderProggressAfterOnlinePayment: goToOrderProggressAfterOnlinePayment,
                        updatePendingOrdersRef: updateOrders,
                        clearOrderProgressContext,
                      }}
                    >
                      <LoadingContext.Provider value={{ loading, setLoading, addressesLoading, setAddressesLoading }}>
                        <MercadoPagoContext.Provider value={{ MercadoPagoInstance }}>
                          <section
                            className={
                              "section-home " +
                              (tabSelected === "home" ? "selected-home " : "") +
                              (!!order && (order.products?.length > 0 || order.beverages?.length > 0) && tabSelected === "home"
                                ? "selected-home-resume-bar "
                                : "")
                            }
                            id={"section-home"}
                          >
                            {errorMessage.length > 0 ? (
                              <div
                                style={{
                                  padding: 20,
                                  marginTop: 40,
                                  color: "#FF0000",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  textAlign: "center",
                                }}
                              >
                                {errorMessage.map((message, key) => (
                                  <h4 key={key}>{message}</h4>
                                ))}
                              </div>
                            ) : (
                              <>
                                {(() => {
                                  switch (tabSelected) {
                                    case "home": {
                                      return (
                                        <>
                                          <Header
                                            setModalCompanies={() =>
                                              setModalCompanies({
                                                status: true,
                                                content: companies,
                                              })
                                            }
                                          />
                                          <Main setModalCompanies={setModalCompanies} />
                                          {siteContent.map((eachItem) => {
                                            return eachItem
                                          })}

                                          <Suspense fallback={<h1>Carregando modal</h1>}>
                                            <ModalBuilder
                                              title="Faça suas escolhas"
                                              type={modalBuilder.type}
                                              status={modalBuilder.status}
                                              content={modalBuilder.content}
                                              onLoad={modalBuilder.onLoad}
                                              onChangeOrder={onChangeOrderBuilder}
                                              onChangeAmountProduct={onChangeAmountProduct}
                                              onClose={() => setModalBuilder({ ...modalBuilder, status: false, content: null, size: null })}
                                            />
                                          </Suspense>
                                        </>
                                      )
                                    }
                                    case "favorites": {
                                      return null
                                    }
                                    case "cart": {
                                      console.info("cart]")
                                      break
                                    }
                                    case "profile": {
                                      return (
                                        <Profile
                                          orderId={orderSuccessId.current}
                                          loading={loading}
                                          setLoading={setLoading}
                                          userConnected={userIsConnected()}
                                        />
                                      )
                                    }
                                    default: {
                                      return <p>Carregando site</p>
                                    }
                                  }
                                })()}
                              </>
                            )}

                            {!!order && (order.products?.length > 0 || order.beverages?.length > 0) && tabSelected === "home" ? (
                              <OrderResumeBar
                                title="Meu pedido"
                                icon="shopping-bag"
                                order={getTotalOrder(order, userSelectedAddress?.deliveryFee)}
                                completeOrder={order}
                                deliveryFee={!!userSelectedAddress && ![3, 2].includes(modality) ? userSelectedAddress.deliveryFee || 0 : 0}
                                setModalSignIn={setModalSignIn}
                                setTab={() => setModalOrderPreview(true)}
                                setModalPaymentMethods={setModalPaymentMethods}
                              />
                            ) : (
                              ""
                            )}
                            {tabSelected !== "cart" ? <Footer setModalContentOrder={setModalContentOrder} /> : ""}

                            {/*Modal de lojas*/}
                            <ModalCompanies
                              title="Onde você está?"
                              content={modalCompanies.content}
                              status={modalCompanies.status}
                              earlyAddress={modalCompanies.earlyAddress}
                              handleOutsideCoverageArea={handleOutsideCoverageArea}
                              onSelectedCompany={onSelectedCompany}
                            />
                            {/*Modal de Login*/}
                            <ModalSignIn
                              status={modalSignIn.status}
                              onSignIn={onSignIn}
                              recoverySettings={recoverySettings}
                              onClose={() => {
                                setModalSignIn({ status: false })
                              }}
                            />
                            {!!modalSignIn ? "" : ""}
                            {/*Modal de Bebidas*/}
                            <ModalBeverages
                              title="Bebidas"
                              content={modalBeverages.content}
                              status={modalBeverages.status}
                              data={modalBeverages.data}
                              category={modalBeverages.category}
                              onClick={onAddBeverages}
                              onClose={() => setModalBeverages({ status: false, content: null })}
                            />
                            {/*Modal para vizualização do pedido*/}
                            <OrderPreview
                              status={modalOrderPreview}
                              openOrderSchedule={onShowModalSchedule}
                              openOrderFinisher={() => setModalOrderFinisher(true)}
                              onClose={() => setModalOrderPreview(false)}
                            />
                            {/*Modal para finalizar o pedido*/}
                            <ModalOrderFinisher status={modalOrderFinisher} finishOrder={finishOrder} onClose={() => setModalOrderFinisher(false)} />
                            {/*Modal de processamento do pedido*/}
                            <ModalOrderProcess
                              status={modalOrderProcess.status}
                              resend={modalOrderProcess.resend}
                              content={modalOrderProcess.content}
                              newRequest={modalOrderProcess.newRequest}
                              scheduleInfo={modalOrderProcess.scheduleInfo}
                              setTabSelected={setTabSelected}
                              finishOrder={finishOrder}
                              insertOrder={insertOrder}
                              onClose={() => {
                                setModalOrderProcess({ status: false, resend: false, newRequest: false, scheduleInfo: null })
                              }}
                            />

                            {/*Modal para finalizar o pedido*/}
                            <ModalOrderSchedule
                              status={modalOrderSchedule}
                              onConfirmOrder={onConfirmOrderSchedule}
                              onClose={() => setModalOrderSchedule(false)}
                              onGetSchedules={getSchedules}
                            />

                            <ModalSuccessOrder
                              content={modalSuccessOrder.content}
                              status={modalSuccessOrder.status}
                              onClose={(orderId) => {
                                orderSuccessId.current = orderId
                                setModalOrderFinisher(false)
                                setModalOrderSchedule(false)
                                setTabSelected("profile")
                                setModalSuccessOrder({ status: false, content: null })
                              }}
                              onBack={() => setModalSuccessOrder({ status: false, content: null })}
                            />

                            {/* Modal para buscar o endereço do cliente ao acessar a página */}
                            <ModalInitialAddress
                              status={modalInitialAddress.status}
                              content={modalInitialAddress.content}
                              onPlaceSelected={onPlaceSelected}
                              onShowMap={() => setModalMapAddress({ status: true, location: {} })}
                              onClose={() => setModalInitialAddress({ status: false, title: "", content: null })}
                              setModalMinimapAddress={setModalMinimapAddress}
                            />
                            <ModalMapAddress
                              status={modalMapAddress.status}
                              location={modalMapAddress.location}
                              address={modalMapAddress?.address}
                              onConfirmLocation={onConfirmLocation}
                              onClose={() => setModalMapAddress({ status: false, location: {} })}
                            />
                            <ModalNumberAddress
                              status={modalNumberAddress.status}
                              address={modalNumberAddress.address}
                              onConfirmNumber={onConfirmNumber}
                              onClose={() => setModalNumberAddress({ status: false, address: {} })}
                            />
                            <ModalFinalAddress
                              status={modalFinalAddress.status}
                              address={modalFinalAddress.address}
                              editNeighbourhood={modalFinalAddress.editNeighbourhood}
                              onConfirmAddress={onConfirmAddress}
                              origin={modalFinalAddress.origin}
                              onClose={() => setModalFinalAddress({ status: false, address: {} })}
                            />

                            {/* Modal para os contatos do estabelecimento.*/}
                            {modalCompanyContacts ? <ModalCompanyContacts onClose={() => setModalCompanyContacts(false)} /> : ""}

                            {/* Modal do popup de mensagem */}
                            <ModalPopup status={modalPopup.status} content={popup} onClose={() => setModalPopup({ status: false })} />

                            {/* Modal de bloqueio devido a versão incorreta*/}
                            <ModalBlocked status={modalBlockByOutdatedVersion.status} content={modalBlockByOutdatedVersion.content} />

                            {loading || addressesLoading || forceLoading ? (
                              <FullContentLoading loading={true} type="fade" title="Aguarde, estamos carregando as informações..." />
                            ) : (
                              ""
                            )}

                            <ModalAddressNotFound
                              status={modalAddressNotFound.status}
                              title={modalAddressNotFound.title}
                              companies={modalAddressNotFound.companies}
                              onUpdate={(company) => {
                                setUserSelectedAddress(null)
                                removeLocalItem("userSelectedAddress")
                                setModalInitialAddress({ status: false, title: "", content: null })
                                setModalMapAddress({ status: false, location: {} })
                                setModalAddressNotFound({ status: false, title: "", companies: [] })
                                getContent(company, false, true).then()
                              }}
                              // onConfirmAddress={onConfirmAddress}
                              onClose={() => setModalAddressNotFound({ status: false, title: "", companies: [] })}
                            />

                            <ModalClientAddressNotFound
                              status={modalClientAddressNotFound.status}
                              address={modalClientAddressNotFound.address}
                              onClose={() => {
                                setModalClientAddressNotFound({ status: false, address: null })
                              }}
                              openAddressModal={(address) => {
                                setModalClientAddressNotFound({ status: false, address: null })

                                setModalFinalAddress({ status: true, address, editNeighbourhood: false, origin: "fromMap" })
                              }}
                            />

                            <ModalPixPayment
                              status={modalPixPayment.status}
                              content={modalPixPayment.content}
                              orderProgressRef={goToOrderProgressRef.current}
                              onClose={() => {
                                clearOrder()
                                setModalPixPayment({ status: false, content: null })
                                setTabSelected("profile")
                                goToOrdersRef.current = true
                              }}
                            />

                            {/* Modal para o componente do minimap do mapbox */}
                            <ModalMinimapAddress
                              status={modalMinimapAddress.status}
                              content={modalMinimapAddress.content}
                              onClose={() => {
                                setModalMinimapAddress({ status: false, content: null })
                              }}
                              onConfirmLocation={onConfirmLocation}
                              setModalMinimapAddress={setModalMinimapAddress}
                            />
                            {!!company && !!company.id_company && (
                              <FacebookPixelManager
                                key={company.id_company}
                                companyId={company.id_company}
                                activePixelId={activePixelId}
                                setActivePixelId={setActivePixelId}
                              />
                            )}

                            {/*/!*Componente de mensagem de alerta*!/*/}
                            {/*<Alert offset={0} stack={{ limit: 3, spacing: 10 }} contentTemplate={AlertTemplate} />*/}
                          </section>
                          <Toaster containerStyle={{ zIndex: 9999 }} />
                        </MercadoPagoContext.Provider>
                      </LoadingContext.Provider>
                    </TabContext.Provider>
                  </OrderContext.Provider>
                </SettingsContext.Provider>
              </CompanyContext.Provider>
            </MenuContext.Provider>
          </UserContext.Provider>
        </SocketContext.Provider>
      </Suspense>
    </ErrorBoundary>
  )
}

export default App
